/* eslint-disable no-unused-vars */
/** -------- packages imports ------ */
import React, { useState, useEffect, useMemo, Suspense } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { useInfiniteQuery, useQuery } from "react-query";
import { AsyncPaginate } from "react-select-async-paginate";
import { useAuthContext } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import useUsersConfig from "hooks/useUsersConfig";
import useReportsFiltersConfig from "hooks/reports/useReportsFiltersConfig";
import { useIsAuthConfigEnabled } from "hooks/useOtpLabel";
/** -------- assets imports -------- */
import { ReactComponent as DownloadIcon } from "../icons/download.svg";
import { ReactComponent as InfoIcon } from "../icons/infoIcon.svg";
import infoIcon from "../assets/img/info.png";
import { tableDetails, tableColumns } from "../constants_map";
/** -------- Component imports -------- */
import Dropdown from "../components/Forms/forminput/Dropdown";
import TabComponent from "../components/Snackbar/Tab/TabComponent";
import ErrorModal from "../components/Modals/ErrorModal";
import {
  interfaceOptions,
  directionOptions,
  emaildirectionOptions,
  statusOptions,
  voiceproductOptions,
  voiceDirectionOptions,
  interfaceVoiceOptions,
  audioSourceOptions,
  finalStatusOptionsForVoice,
  campaignCategoryOptionsForVoice,
  interfaceWAOptions,
  waDirectionOptions,
  interfaceEmailOptions,
  waStatusOptions,
  channelOptions,
  smsEmailCategoryOptions,
} from "../reports_data";
import {
  getReports,
  getUsersForReports,
  getUsersForReportsEmail,
  getEnterprisesForReports,
  getProvidersForReport,
  getProviderForBind,
  getOfflineReports,
  getEmailOfflineReports,
  cancelOfflineReports,
  getPASenderAddressListForReport,
  getPAEmailAddressListForReport,
  getEmailAddressesForReport,
  getAllEmailAddressByuser,
  getSenderAddressesForReport,
  getAllSenderAddressByuser,
  getVoiceReports,
  getEMAILReports,
  getOfflineVoiceReports,
  getPackageNames,
  getRateSheetNames,
  getCallerIds,
  getCampaignNames,
  getVoiceCapabilityNumberList,
  getVREnabledNumberList,
  getPackageTypes,
  downloadFileFromUrl,
  getWAReports,
  getWAOfflineReports,
  getClients,
  getCampList,
  getTemplateList,
} from "../lib/report-service-apis";
import {
  getTimeZoneInHours,
  getLocalFormattedDate,
  getLocalFormattedDate24Hr,
} from "../utils/DateOrdinals";
import Navigation from "../components/Navigation/Navigation";
import MainDiv from "containers/MainDiv";
import PrimaryButton from "components/Button/PrimaryButton";
import SecondaryButton from "components/Button/SecondaryButton";
import IconButton from "components/Button/IconButton";
import FilterDate from "components/Popover/FilterDate";
import PageContainer from "containers/PageContainer";
import PageItems from "containers/PageItems";
import OverFlowTooltip from "components/Table/OverFlowTooltip";
import InfiniteTable from "components/Table/InfiniteTable";
import MultiSelectPaginatedDropdown from "components/Dropdowns/MultiSelectPaginatedDropdown";
import { isNumber } from "utils/MathHelpers";
import DownloadReportModal from "../components/Modals/DownloadReportModal";
import useIsReportNamePromptEnabled from "../hooks/reports/useReportNamePrompt";

dayjs.extend(utc);
dayjs.extend(tz);
const nameMaxWidth = "250px";
let uniqueCampaignNameSet = new Set();
let uniqueTemplateSet = new Set();

const reportTypes = {
  SUMMARY: "summary",
  DETAILED: "detailed",
};

function translateOptionsLabel(options = []) {
  try {
    if (!Array.isArray(options)) {
      return console.log(`[ERROR] options are not an Array:`, options);
    }
    return options.map((m) => ({
      ...m,
      label: t(m.label) ? t(m.label) : m.label,
    }));
  } catch (error) {
    console.log(`[ERROR] Not able to translate: `, error);
    return [];
  }
}

const isWAChannel = (channel) => channel === "wa";
const isEmailChannel = (channel) => channel === "email";
const isVoiceChannel = (channel) => channel === "voice";

const dropdownNames = {
    INTERFACE: "interface",
    CATEGORY: "category", // direction
    STATUS: "status",
    CAMPAIGN_CATEGORY: "campaignCategoryFilter",
  },
  channels = {
    WA: "wa",
    EMAIL: "email",
    VOICE: "voice",
  };
//
function getOptions(name, channel) {
  const optionsMap = {
    [dropdownNames.INTERFACE]: {
      voice: interfaceVoiceOptions,
      wa: interfaceWAOptions,
      email: interfaceEmailOptions,
      DEFAULT: interfaceOptions,
    },
    [dropdownNames.CATEGORY]: {
      voice: voiceDirectionOptions,
      wa: waDirectionOptions,
      email: emaildirectionOptions,
      DEFAULT: directionOptions,
    },
    [dropdownNames.STATUS]: {
      wa: waStatusOptions,
      DEFAULT: statusOptions,
    },
    [dropdownNames.CAMPAIGN_CATEGORY]: {
      DEFAULT: smsEmailCategoryOptions,
    },
  };
  const dropdownObj = optionsMap[name];
  if (!dropdownObj) {
    console.log(`[ERROR] Invalid dropdown name: ${name}`);
    return [];
  }

  const opts = dropdownObj[channel] || dropdownObj.DEFAULT || [];
  if (!opts) {
    console.log(`[ERROR] Invalid dropdown channel map: ${channel}`);
    return [];
  }
  const tOptions = translateOptionsLabel(opts);
  return tOptions;
}

const InfoModal = (props) => {
  const styles = {
    modalstyle: `absolute flex z-10 bg-${
      props.type === "th" ? "[#F0F0F0]" : "white"
    } justify-center shadow-[0_3px_8px_rgba(0,0,0,0.24)] rounded-xl max-w-xs`,
  };

  return (
    <div
      className={styles.modalstyle}
      style={{
        boxShadow: `0px 3px 6px ${
          props.type === "th" ? "#00000029" : "#0000001A"
        }`,
      }}
    >
      <div className="p-1 rounded-xl relative w-max">{props.children}</div>
    </div>
  );
};

/** Table <td> cell with overflow tooltip  */
const TableCellWithTooltip = ({ children }) => {
  return (
    <td className={tableColumns.defautCell}>
      <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
        {children}
      </OverFlowTooltip>
    </td>
  );
};

const PaginatedDropdown = (props) => {
  const { userDetails } = useAuthContext();
  const { theme } = useTheme();
  const {
    name,
    value,
    setFieldTouched,
    apiCall,
    channel,
    setValues,
    placeholder,
    country,
    user,
    accountId,
  } = props;
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "2.75rem",
      backgroundColor: "inherit",
      borderRadius: "0.25rem",
      border: state.isFocused
        ? `1px solid ${theme.secondaryColor}`
        : "1px solid #cacaca",

      boxShadow: state.isFocused ? `0 0 2px ${theme.secondaryColor}` : "none",
      "&:hover": {
        borderColor: theme.secondaryColor,
        boxShadow: `0 0 2px ${theme.secondaryColor}`,
      },
    }),
    option: (provided) => ({
      ...provided,
      color: "black",
      background: "white",
      ":hover": {
        background: "#F3F3F3",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      fontSize: "14px",
      // fontWeight: "600",
      color: "rgb(202, 202, 202)",
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: theme.backgroundColor,
      fontSize: "14px",
      border: "1px solid #CACACA",
      boxShadow: "0 !important",
      overflow: "hidden",
      "&:hover": {
        border: "1px solid #CACACA",
      },
      zindex: 999,
    }),
    menuList: (provided) => ({
      ...provided,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    multiValueRemove: (base) => ({
      ...base,
      backgroundColor: "#4B93FF",
      color: "white",
      borderTopRightRadius: "20px",
      borderBottomRightRadius: "20px",
      "&:hover": {
        backgroundColor: "#4B93FF",
        color: "white",
      },
    }),
    multiValueLabel: (data) => ({
      ...data,
      color: "white !important",
    }),
    multiValue: (data) => ({
      ...data,
      backgroundColor: "#4B93FF",
      borderRadius: "20px",
    }),
    input: (data) => ({
      ...data,
      "input:focus": {
        boxShadow: "none",
      },
      boxShadow: "0 !important",
      "&:hover": {
        border: "0 !important",
      },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };
  const loadOptions = async (search, prevOptions, { page: pgIndex }) => {
    let apiResponse;
    if (name === "sender") {
      apiResponse = await apiCall(
        search,
        pgIndex,
        userDetails.token,
        country,
        user === "self" || user === "all" || user === ""
          ? userDetails.sub
          : user
      );
    } else if (name === "email") {
      apiResponse = await apiCall(
        search,
        pgIndex,
        userDetails.token,
        user === "self" ? userDetails.sub : user
      );
    } else if (name === "whatsAppnumber") {
      apiResponse = await apiCall(
        search,
        pgIndex,
        userDetails.token,
        country,
        user === "self" ? null : user
      );
    } else {
      apiResponse = await apiCall(
        search,
        prevOptions,
        pgIndex,
        userDetails.token,
        channel,
        userDetails.given_name,
        userDetails.email,
        accountId
      );
    }
    return apiResponse;
  };

  return (
    <AsyncPaginate
      loadOptions={loadOptions}
      additional={{
        page: 1,
      }}
      name={name}
      styles={customSelectStyles}
      placeholder={placeholder}
      components={{
        IndicatorSeparator: () => null,
      }}
      menuPortalTarget={document.body}
      onChange={(e) => {
        setValues((prevValues) => ({
          ...prevValues,
          [name]: e,
          ...((name === "user" || name === "country") && { sender: "" } && {
              whatsAppnumber: "",
            }),
          ...(name === "provider" && { bind: "" }),
        }));
      }}
      onBlur={() => setFieldTouched(name, true)}
      value={value}
      isDisabled={props.isDisabled}
      cacheUniqs={[name, value, apiCall, user, country, channel, accountId]}
    />
  );
};

const GenerateReport = (props) => {
  const {
    values,
    data,
    isLoading,
    userDetails,
    setIsOfflineReportTableEnabled,
    isRefreshDisabled,
  } = props;
  const { theme } = useTheme();

  let sortedData =
    data?.data?.sort(
      (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt)
    ) || [];

  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "  lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b   border-gray-200",
  };
  const headers = [
    t("_Channel"),
    t("_Requested_On"),
    // t("Requested by"),
    t("_Status"),
    t("_Actions"),
  ];

  return (
    <div>
      <InfiniteTable
        dataLength={tableDetails.pageSize}
        pageStart={0}
        isLoading={isLoading || isRefreshDisabled}
        initialLoad={false}
        scrollThreshold={0.5}
        hasMore={true}
        height="50vh"
        count={sortedData?.length}
      >
        <table className={`${tableDetails.scrollableTable} tablenowrap`}>
          <thead>
            <tr>
              {headers.map((ele, i) => {
                return (
                  <th
                    className={
                      ele === t("_Actions")
                        ? `${tableColumns._Actions} w-24`
                        : ""
                    }
                    key={i}
                  >
                    {ele}
                  </th>
                );
              })}
            </tr>
          </thead>
          {!isRefreshDisabled && !isLoading && (
            <tbody>
              {sortedData?.map((ele, i) => {
                return (
                  <tr id={i} key={i}>
                    <td className={tableColumns.defautCell}>
                      {ele?.channel
                        ? ele?.channel === "VOICE_DTMF"
                          ? "VOICE"
                          : ele.channel
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {(ele?.createdAt &&
                        dayjs(ele?.createdAt)
                          .utc("z")
                          .local()
                          .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
                          .format(theme.dateFormat + " " + theme.timeFormat)) ||
                        "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele?.status || "NA"}
                    </td>
                    <td className={tableColumns._Actions}>
                      {ele.status === "COMPLETED" ? (
                        <div
                          className="text-xs flex justify-center cursor-pointer"
                          style={{ color: props.theme.secondaryColor }}
                          onClick={() =>
                            downloadFileFromUrl(
                              userDetails?.token,
                              ele.reportUrl,
                              ele?.name || "ReportData"
                            )
                          }
                        >
                          <DownloadIcon
                            style={{ color: props.theme.secondaryColor }}
                            className="mx-3 h-4 w-4"
                          />
                        </div>
                      ) : ele.status === "PENDING" ||
                        ele.status === "PROCESSING" ? (
                        <div
                          className="text-red-500"
                          onClick={(e) => {
                            cancelOfflineReports(
                              userDetails.token,
                              values.channel,
                              ele.id
                            )
                              .then((res) => {
                                setIsOfflineReportTableEnabled(true);
                              })
                              .catch((err) => {
                                console.log(
                                  "error while trying to cancel offline report: ",
                                  err,
                                  err?.response
                                );
                              });
                          }}
                        >
                          {t("_CANCEL")}
                        </div>
                      ) : (
                        "NA"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </InfiniteTable>
    </div>
  );
};

const PreviewSampleForDetails = (props) => {
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    hasNextPage,
    loggedInRole,
    direction,
  } = props;
  const isMoDirection = direction === "MO";
  const { theme } = useTheme();
  const enableAuthConfig = useIsAuthConfigEnabled();

  const styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "  lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b   border-gray-200",
  };

  const unfilteredHeaders = [
    loggedInRole === "PA" ? t("_Account") : "", //accName
    loggedInRole !== "EU" ? t("_User") : "", //user
    loggedInRole !== "EU" ? t("User_Email") : "", //user email
    t("_Transaction_ID"), //Message Id
    t("_Accepted_Time"), //accepted_time
    t("_Direction"), //direction
    t("_Channel"), //channel_type
    t("_Sender/Number"), //sender_address
    t("_Country"), //Country
    t("_MSISDN"), //mobile_number
    t("_Content_Type"), //content type
    t("_Interface"), //Interface type
    t("_Category"), //Campaign category
    t("_AuthConfig_Profile"), //AuthConfig Profile
    t("_Campaign_Type"), //Job type
    t("_Name"), //campaignname
    t("_Campaign_ID"), //Parent job id
    t("_Client_Txn_ID"), //Client Txn ID
    t("_Recurrence_ID"), //Recurrence ID
    t("_Message_Template"), //Message template
    t("_Fragment_Count"), //Fragment Count
    isMoDirection ? t("_Admin_Fee") : "", //cost
    isMoDirection ? t("_S_Charge") : "", //s_chr
    isMoDirection ? t("_Fragment") : "", //f_cnt
    t("_Name"), //package_name
    t("_Package_Type"), //Package type
    t("_Package_Units"), //package_unit
    t("_Rate_Sheets"), //ratesheetname
    t("_Currency"), //Currency
    t("Rate_Sheet_Per_Unit_Price"), //rate_sheet_per_unit
    t("Total_Price"), //total_price
    loggedInRole === "PA" ? t("_Provider") : "", //Provider
    loggedInRole === "PA" ? t("_Bind") : "", //Bind
    loggedInRole === "PA" ? t("Unit_Cost") : "", //unit_cost
    loggedInRole === "PA" ? t("Total_Cost") : "", //total_cost
    t("Submitted_Time"), //submitted_time
    t("Delivered_Time"), //delivered_time
    t("Status_Code"), //status_code
    t("_Status"), //status
    t("Failure_Reason"), //Desc
  ];

  const headers = unfilteredHeaders.filter((e) => e !== "");

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <div className="w-[77vw]">
      <InfiniteTable
        dataLength={() => dataLength()}
        pageStart={0}
        initialLoad={false}
        height="56vh"
        className="overflow-auto show-scrollbar"
        scrollThreshold={0.5}
        next={fetchNextPage}
        hasMore={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        count={allData.length}
      >
        <table className={`${tableDetails.xsClassName} tablenowrap`}>
          <thead>
            <tr>
              {headers.map((ele, i) => {
                return (
                  <th key={i}>
                    <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                      {ele}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!isLoading &&
              data?.pages?.map((e, i) =>
                e?.response?.data?.data?.map((ele) => (
                  <tr id={ele.id} key={i}>
                    {loggedInRole === "PA" && (
                      <td className={tableColumns.defautCell}>
                        <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                          {ele.accName || "NA"}
                        </OverFlowTooltip>
                      </td>
                    )}
                    {loggedInRole !== "EU" && (
                      <td className={tableColumns.defautCell}>
                        <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                          {ele.user || "NA"}
                        </OverFlowTooltip>
                      </td>
                    )}
                    {loggedInRole !== "EU" && (
                      <td className={tableColumns.defautCell}>
                        {ele["user email"] || "NA"}
                      </td>
                    )}
                    <td className={tableColumns.defautCell}>
                      {ele["Message Id"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele.accepted_time && !isNaN(ele.accepted_time)
                        ? dayjs(new Date(parseInt(ele.accepted_time))).format(
                            theme.dateFormat + " " + theme.timeFormat
                          )
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["direction"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["channel_type"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["sender_address"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Country"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["mobile_number"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["content_tp"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Interface type"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Campaign category"] &&
                      ele["Campaign category"] !== "NA"
                        ? enableAuthConfig && ele["Campaign category"] === "OTP"
                          ? "Authentication"
                          : ele["Campaign category"]
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["AuthConfig Profile"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Job Type"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["campaignname"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Parent job id"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Client Txn ID"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Recurrence ID"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Message template"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Fragment Count"] || ele["Fragment Count"] == "0"
                        ? ele["Fragment Count"]
                        : "NA"}
                    </td>
                    {isMoDirection && (
                      <>
                        <td className={tableColumns.defautCell}>
                          {ele.cost || ele.cost == "0" ? ele.cost : "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.s_chr || ele.s_chr == "0" ? ele.s_chr : "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.f_cnt || ele.f_cnt == "0" ? ele.f_cnt : "NA"}
                        </td>
                      </>
                    )}
                    <td className={tableColumns.defautCell}>
                      {ele.package_name?.split(":")?.[0] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Package type"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele.package_unit || ele.package_unit == "0"
                        ? ele.package_unit
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele.ratesheetname || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Currency"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["rate_sheet_per_unit"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {isNumber(ele.total_price)
                        ? parseFloat(ele.total_price).toFixed(2)
                        : ele.total_price || "NA"}
                    </td>
                    {loggedInRole === "PA" && (
                      <>
                        <td className={tableColumns.defautCell}>
                          {ele.Provider || "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.Bind || "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {isNumber(ele.unit_cost)
                            ? parseFloat(ele.unit_cost).toFixed(2)
                            : ele.unit_cost || "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {isNumber(ele.total_cost)
                            ? parseFloat(ele.total_cost).toFixed(2)
                            : ele.total_cost || "NA"}
                        </td>
                      </>
                    )}
                    <td className={tableColumns.defautCell}>
                      {ele.submitted_time && !isNaN(ele.submitted_time)
                        ? dayjs(new Date(parseInt(ele.submitted_time))).format(
                            theme.dateFormat + " " + theme.timeFormat
                          )
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele.delivered_time && !isNaN(ele.delivered_time)
                        ? dayjs(new Date(parseInt(ele.delivered_time))).format(
                            theme.dateFormat + " " + theme.timeFormat
                          )
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["status_code"] || ele["status_code"] == "0"
                        ? ele["status_code"]
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["status"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Desc"] || "NA"}
                    </td>
                  </tr>
                ))
              )}
            {/* {data?.pages?.length === 1 &&
              data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                <div>No data available</div>
              )} */}
          </tbody>
        </table>
      </InfiniteTable>
    </div>
  );
};

const PreviewSampleForSummary = (props) => {
  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "  lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b   border-gray-200",
  };
  const { loggedInRole, direction } = props;
  const isMoDirection = direction === "MO";
  const filterConfig = useReportsFiltersConfig();

  const headers = [
    t("_Date"), //timestamp
    t("_Campaign_ID"), //p_id
    t("_Campaign_Name"), //c_nm
    loggedInRole === "PA" ? t("_Account") : "", //accName
    loggedInRole === "EA" ? t("_User") : "", //user
    filterConfig.emailFilterEnabled ? t("_Email") : "",
    filterConfig.orgIdFilterEnabled ? t("_Organization_ID") : "",
    t("_Direction"), //direction
    t("_Channel"), //ch_tp
    t("_Country"), //country
    t("_Sender"), //s_adr
    loggedInRole === "PA" ? t("_Provider") : "", //provider
    t("_Interface"), //i_tp
    t("_Total_Sent"), //total_sent
    t("_Total_Received"), //total_received
    isMoDirection ? t("_Admin_Fee") : "", //cost
    isMoDirection ? t("_S_Charge") : "", //s_chr
    isMoDirection ? t("_Fragment") : "", //f_cnt
    t("_Submit_Success"), //submit_success
    t("_Submit_Failed"), //total_failed
    t("_Delivery_Success"), //dr_success
    t("_Delivery_Failed"), //dr_failed
    t("_Success_(%)"), //success_percentage
  ].filter((e) => e !== "");

  const { data, isFetchingNextPage, fetchNextPage, isLoading, hasNextPage } =
    props;

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <>
      <div className="w-[77vw]">
        <InfiniteTable
          dataLength={() => dataLength()}
          pageStart={0}
          initialLoad={false}
          scrollThreshold={0.5}
          next={fetchNextPage}
          hasMore={hasNextPage}
          height="56vh"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          count={allData.length}
        >
          <table className={`${tableDetails.scrollableTable} tablenowrap`}>
            <thead>
              <tr>
                {headers.map((ele, i) => {
                  return (
                    <th key={i}>
                      <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                        {ele}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                data?.pages?.map((e, i) =>
                  e?.response?.data?.data?.map((ele) => (
                    <tr id={ele.id} key={i}>
                      <td className={tableColumns.defautCell}>
                        {ele.timestamp && !isNaN(ele.timestamp)
                          ? dayjs(new Date(parseInt(ele.timestamp))).format(
                              "YYYY-MM-DD"
                            )
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.p_id || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.c_nm || "NA"}
                      </td>
                      {loggedInRole === "PA" && (
                        <TableCellWithTooltip>
                          {ele.accName || "NA"}
                        </TableCellWithTooltip>
                      )}
                      {loggedInRole === "EA" && (
                        <TableCellWithTooltip>
                          {ele.user || "NA"}
                        </TableCellWithTooltip>
                      )}
                      {filterConfig.emailFilterEnabled && (
                        <TableCellWithTooltip>
                          {ele.userEmail || "-"}
                        </TableCellWithTooltip>
                      )}
                      {filterConfig.orgIdFilterEnabled && (
                        <TableCellWithTooltip>
                          {ele.o_id || "-"}
                        </TableCellWithTooltip>
                      )}
                      <td className={tableColumns.defautCell}>
                        {ele.direction || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.ch_tp || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.country || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.s_adr || "NA"}
                      </td>
                      {loggedInRole === "PA" && (
                        <td className={tableColumns.defautCell}>
                          {ele.provider || "NA"}
                        </td>
                      )}
                      <td className={tableColumns.defautCell}>
                        {ele.i_tp === "WEB" ? t("PORTAL") : ele.i_tp || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.total_sent || ele.total_sent == "0"
                          ? ele.total_sent
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.total_received || ele.total_received == "0"
                          ? ele.total_received
                          : "NA"}
                      </td>
                      {isMoDirection && (
                        <>
                          <td className={tableColumns.defautCell}>
                            {ele.cost || ele.cost == "0" ? ele.cost : "NA"}
                          </td>
                          <td className={tableColumns.defautCell}>
                            {ele.s_chr || ele.s_chr == "0" ? ele.s_chr : "NA"}
                          </td>
                          <td className={tableColumns.defautCell}>
                            {ele.f_cnt || ele.f_cnt == "0" ? ele.f_cnt : "NA"}
                          </td>
                        </>
                      )}
                      <td className={tableColumns.defautCell}>
                        {ele.submit_success || ele.submit_success == "0"
                          ? ele.submit_success
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.total_failed || ele.total_failed == "0"
                          ? ele.total_failed
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.dr_success || ele.dr_success == "0"
                          ? ele.dr_success
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.dr_failed || ele.dr_failed == "0"
                          ? ele.dr_failed
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.success_percentage || ele.success_percentage == "0"
                          ? ele.success_percentage
                          : "NA"}
                      </td>
                    </tr>
                  ))
                )}
              {/* {data?.pages?.length === 1 &&
                data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                  <div>No data available</div>
                )} */}
            </tbody>
          </table>
        </InfiniteTable>
      </div>
    </>
  );
};

const PreviewSampleForSummaryForVoice = (props) => {
  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "  lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b   border-gray-200",
  };
  const { loggedInRole, productType } = props;
  const filterConfig = useReportsFiltersConfig();

  const headers = [
    "Date", //time
    loggedInRole === "PA" ? t("_Account") : "", //accName
    loggedInRole === "EA" ? "User" : "", //user
    filterConfig.emailFilterEnabled ? t("_Email") : "",
    filterConfig.orgIdFilterEnabled ? t("_Organization_ID") : "",
    "Direction", //direction
    "Channel", //ch_tp
    "Country", //country
    "Caller Id", //s_adr
    loggedInRole === "PA" ? t("_Provider") : "", //provider
    "Interface", //i_tp
    "Total Sent", //total_sent
    "Total Success", //dr_success
    "Total Failed", //total_failed
    "Success (%)", //success_percentage
  ].filter((e) => e !== "");

  const { data, isFetchingNextPage, fetchNextPage, isLoading, hasNextPage } =
    props;

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <>
      <div className="w-[77vw]">
        <InfiniteTable
          dataLength={() => dataLength()}
          pageStart={0}
          initialLoad={false}
          scrollThreshold={0.5}
          next={fetchNextPage}
          hasMore={hasNextPage}
          height="56vh"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          count={allData.length}
        >
          <table className={`${tableDetails.scrollableTable} tablenowrap`}>
            <thead>
              <tr>
                {headers.map((ele, i) => {
                  return (
                    <th key={i}>
                      <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                        {ele}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                data?.pages?.map((e, i) =>
                  e?.response?.data?.data?.map((ele) => {
                    return (
                      <tr id={ele.id} key={i}>
                        <td className={tableColumns.defautCell}>
                          {ele.time || "NA"}
                        </td>
                        {loggedInRole === "PA" && (
                          <td className={tableColumns.defautCell}>
                            <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                              {ele.accName || "NA"}
                            </OverFlowTooltip>
                          </td>
                        )}
                        {loggedInRole === "EA" && (
                          <td className={tableColumns.defautCell}>
                            <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                              {ele.user || "NA"}
                            </OverFlowTooltip>
                          </td>
                        )}
                        {filterConfig.emailFilterEnabled && (
                          <TableCellWithTooltip>
                            {ele.userEmail || "-"}
                          </TableCellWithTooltip>
                        )}
                        {filterConfig.orgIdFilterEnabled && (
                          <TableCellWithTooltip>
                            {ele.o_id || "-"}
                          </TableCellWithTooltip>
                        )}
                        <td className={tableColumns.defautCell}>
                          {ele.direction || "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.ch_tp || "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.country || "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.c_id_num || "NA"}
                        </td>
                        {loggedInRole === "PA" && (
                          <td className={tableColumns.defautCell}>
                            {ele.provider || "NA"}
                          </td>
                        )}
                        <td className={tableColumns.defautCell}>
                          {ele.i_tp === "WEB" ? t("PORTAL") : ele.i_tp || "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.total_sent || ele.total_sent == "0"
                            ? ele.total_sent
                            : "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.submit_success || ele.submit_success == "0"
                            ? ele.submit_success
                            : "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.total_failed || ele.total_failed == "0"
                            ? ele.total_failed
                            : "NA"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele.success_percentage ||
                          ele.success_percentage == "0"
                            ? ele.success_percentage
                            : "NA"}
                        </td>
                      </tr>
                    );
                  })
                )}
              {/* {data?.pages?.length === 1 &&
                data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                  <div>No data available</div>
                )} */}
            </tbody>
          </table>
        </InfiniteTable>
      </div>
    </>
  );
};
const PreviewSampleForDetailsForVoice = (props) => {
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    hasNextPage,
    loggedInRole,
    productType,
  } = props;

  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b border-gray-200",
  };

  // let dynamicHeaders = [];
  // if (detailsData && detailsData?.pages[0]?.response) {
  //   const keys =
  //     detailsData?.pages?.[0]?.response?.data?.data?.length > 0
  //       ? Object.keys(detailsData?.pages[0]?.response?.data?.data?.[0])
  //       : [];
  //   keys.length &&
  //     keys.forEach((e) => {
  //       dynamicHeaders.push(e);
  //     });
  // }

  const VoicePAHeaders = [
    t("_Account"),

    "Caller Id",
    "MSISDN",
    "Time",
    "Interface", //Interface type
    "Campaign Category", //Campaign category

    "Audio Source",

    "Final Status",
  ];
  const VoiceEAHeaders = [
    "User Name",
    "Direction",
    "Channel Type",
    "Interface",
    "Campaign Category",

    "Final Status",
  ];
  const VRPAHeaders = [
    "Account Name",
    "Customer No.",
    "Enterprise No.",
    "Enterprise User No.",
    "Direction",
    "Time",
    "Final Status",
  ];
  const VREAHeaders = [
    "User Name",
    "Channel Type",
    "Customer No.",
    "Enterprise No.",
    "Enterprise User No.",
    "Direction",
    "Time",
    "Final Status",
    "Total Cost",
  ];

  const headers =
    loggedInRole === "PA"
      ? productType === "VR"
        ? VRPAHeaders
        : VoicePAHeaders
      : productType === "Voice"
      ? VoiceEAHeaders
      : VREAHeaders;

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <div className="w-[77vw]">
      <InfiniteTable
        dataLength={() => dataLength()}
        pageStart={0}
        initialLoad={false}
        height="56vh"
        scrollThreshold={0.5}
        next={fetchNextPage}
        hasMore={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        count={allData.length}
      >
        <table className={`${tableDetails.xsClassName} tablenowrap`}>
          <thead>
            <tr>
              {headers.map((ele, i) => {
                return (
                  <th key={i}>
                    <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                      {ele}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="overflow-x-auto">
            {!isLoading &&
              data?.pages?.map((e, i) =>
                e?.response?.data?.data?.map((ele) => {
                  if (loggedInRole === "PA" && productType === "VR") {
                    return (
                      <tr id={ele.id} key={i}>
                        <TableCellWithTooltip>
                          {ele["Account Name"] || "-"}
                        </TableCellWithTooltip>
                        <td className={tableColumns.defautCell}>
                          {ele["Customer Number"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Enterprise Number"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Enterprise User Number"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Call Direction"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Call Start Time"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Final Status"] || "-"}
                        </td>
                      </tr>
                    );
                  } else if (loggedInRole === "PA" && productType === "Voice") {
                    return (
                      <tr id={ele.id} key={i}>
                        <td className={tableColumns.defautCell}>
                          {ele["Account Name"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Caller ID Number"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele[t("_Mobile_Number")] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Call Start Time"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Interface type"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Campaign category"] || "-"}
                        </td>

                        <td className={tableColumns.defautCell}>
                          {ele["Audio Source"] || "-"}
                        </td>

                        <td className={tableColumns.defautCell}>
                          {ele["Final Status"] || "-"}
                        </td>
                      </tr>
                    );
                  } else if (
                    (loggedInRole === "EA" || loggedInRole === "EU") &&
                    productType === "Voice"
                  ) {
                    return (
                      <tr id={ele.id} key={i}>
                        <td className={tableColumns.defautCell}>
                          {ele["User Name"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Call Direction"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Channel type"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Interface type"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Campaign category"] || "-"}
                        </td>

                        <td className={tableColumns.defautCell}>
                          {ele["Final Status"] || "-"}
                        </td>
                      </tr>
                    );
                  } else if (
                    (loggedInRole === "EA" || loggedInRole === "EU") &&
                    productType === "VR"
                  ) {
                    return (
                      <tr id={ele.id} key={i}>
                        <td className={tableColumns.defautCell}>
                          {ele["User Name"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Channel type"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Customer Number"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Enterprise Number"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Enterprise User Number"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Call Direction"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Call Start Time"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {ele["Final Status"] || "-"}
                        </td>
                        <td className={tableColumns.defautCell}>
                          {isNumber(ele["Total Price"])
                            ? parseFloat(ele["Total Price"]).toFixed(2)
                            : ele["Total Price"] || "-"}
                        </td>
                      </tr>
                    );
                  }
                })
              )}
            {/* {data?.pages?.length === 1 &&
                data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                  <div>No data available</div>
                )} */}
          </tbody>
        </table>
      </InfiniteTable>
    </div>
  );
};

const PreviewSampleForDetailsForWA = (props) => {
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    hasNextPage,
    loggedInRole,
    theme,
  } = props;

  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b border-gray-200",
  };

  const unfilteredHeaders = [
    "Category", //Category
    "AuthConfig Profile", //AuthConfig Profile
    "Interface", // Interface type
    "Channel",
    loggedInRole === "PA" ? "Account" : "", //Account
    loggedInRole !== "EU" ? "User" : "", //User
    "Country", //Country
    "Template Name",
    "Template ID",
    "Template Type",
    "Language",
    "Inbound Message", //Inbound Message
    "Quick Reply Button Click", //Quick Reply Button Click
    "Quick Reply Button Clicked Content", //Quick Reply Button Clicked Content
    "Recurrence ID", //Recurrence ID
    "Campaign ID", //Campaign ID
    "Campaign Name", //Campaign Name
    "Conversation ID", //Conversation ID
    "Message ID", //Message ID
    "Transaction ID", //Transaction ID
    "Direction", //Direction
    "From", //Sender
    "To", //MSISDN
    "Status", //Status
    "Failure Reason", //Failure Reason
    "Package Name", //Package Name
    "Package Units", //Package Units
    "Rate Sheet", //Rate Sheet
    "Rate Sheet Per Unit Price", //rate_sheet_per_unit
    // "Accepted Time", //Accepted Time
    "Submitted Time", //Submitted Time
    "Delivery Time", //Delivery Time
    "Read Time", //Read Time
    "Business Scope UserId", //Business Scope User Id
  ];

  const headers = unfilteredHeaders.filter((e) => e !== "");

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <div className="w-[77vw]">
      <InfiniteTable
        dataLength={() => dataLength()}
        pageStart={0}
        initialLoad={false}
        height="56vh"
        scrollThreshold={0.5}
        next={fetchNextPage}
        hasMore={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        count={allData.length}
      >
        <table className={`${tableDetails.xsClassName} tablenowrap`}>
          <thead>
            <tr className="sticky bg-white top-0 z-[1]">
              {headers.map((ele, i) => {
                return (
                  <th
                    className="py-4 ltr:text-left rtl:text-right text-sm text-[#7B7B7B] font-semisolid pr-5"
                    key={i}
                  >
                    <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                      {ele}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="overflow-x-auto">
            {!isLoading &&
              data?.pages?.map((e, i) =>
                e?.response?.data?.data?.map((ele) => (
                  <tr id={ele.id} key={i}>
                    <td className={tableColumns.defautCell}>
                      {ele["Category"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["AuthConfig Profile"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Interface type"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Channel"] || "WHATSAPP"}
                    </td>
                    {loggedInRole === "PA" && (
                      <td className={tableColumns.defautCell}>
                        <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                          {ele.Account || "NA"}
                        </OverFlowTooltip>
                      </td>
                    )}
                    {loggedInRole !== "EU" && (
                      <td className={tableColumns.defautCell}>
                        <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                          {ele.User || "NA"}
                        </OverFlowTooltip>
                      </td>
                    )}
                    <td className={tableColumns.defautCell}>
                      {ele["Country"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Template Name"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Template ID"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {t(ele["Template Type"]) || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Language"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                        {ele["Inbound Message"] || "NA"}
                      </OverFlowTooltip>
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Quick Reply Button Click"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Quick Reply Button Clicked Content"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Recurrence ID"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Campaign ID"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Campaign Name"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Conversation ID"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Message ID"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Transaction ID"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Direction"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["From"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["To"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Status"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Failure Reason"] || "NA"}
                    </td>

                    <td className={tableColumns.defautCell}>
                      {ele["Package Name"]?.split(":")?.[0] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Package Units"] || ele["Package Units"] == "0"
                        ? ele["Package Units"]
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Rate Sheet"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Rate Sheet Per Unit Price"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {getLocalFormattedDate24Hr(
                        ele["Submitted Time"] || "NA",
                        theme.dateFormat,
                        theme.timeFormat
                      )}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {getLocalFormattedDate24Hr(
                        ele["Delivery Time"] || "NA",
                        theme.dateFormat,
                        theme.timeFormat
                      )}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {getLocalFormattedDate24Hr(
                        ele["Read Time"] || "NA",
                        theme.dateFormat,
                        theme.timeFormat
                      )}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Business Scope User Id"] || "NA"}
                    </td>
                  </tr>
                ))
              )}
            {/* {data?.pages?.length === 1 &&
                data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                  <div>No data available</div>
                )} */}
          </tbody>
        </table>
      </InfiniteTable>
    </div>
  );
};

const PreviewSampleForSummaryForWA = (props) => {
  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "  lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b   border-gray-200",
  };
  const { loggedInRole } = props;
  const { globalConfig } = props;
  const filterConfig = useReportsFiltersConfig();

  let isCBP = globalConfig?.some(
    (val) => val?.key === "waPricingType" && val?.value === "CBP"
  );

  const headers = [
    "Channel",
    "Date", //timestamp
    "Campaign Name", //c_nm
    "Country", //country
    loggedInRole === "PA" ? t("_Account") : "", //accName
    "User", //user
    filterConfig.emailFilterEnabled ? t("_Email") : "",
    filterConfig.orgIdFilterEnabled ? t("_Organization_ID") : "",
    "Interface", //i_tp
    "Direction", //dir
    "WABA Phone Number", //wa_ba
    isCBP ? "Type of Conversations" : "Category", //m_ctg
    //props?.direction === "12" ? "" : "Total Sent", //total_sent
    "Total Sent",
    "Total Submit Success", //submit_success
    "Total Delivered",
    //props?.direction === "12" ? "" : "Total Read", //msg_read
    "Total Read",
    "Delivery (%)", //success_percentage
    isCBP ? "" : "Total Free Messages",
    isCBP ? "" : "Total Paid Messages",
    "Total Submit Failure",
    "Total Delivery Failure", //submit_failed
    "Total New Conversations", //total_conv
  ].filter((e) => e !== "");

  const { data, isFetchingNextPage, fetchNextPage, isLoading, hasNextPage } =
    props;

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const epocToJsDate = (ts) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sept",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${new Date(ts).getFullYear()}-${
      months[new Date(ts).getMonth()]
    }-${new Date(ts).getDate()}`;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <>
      <div className="w-[77vw]">
        <InfiniteTable
          dataLength={() => dataLength()}
          pageStart={0}
          initialLoad={false}
          scrollThreshold={0.5}
          next={fetchNextPage}
          hasMore={hasNextPage}
          height="56vh"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          count={allData.length}
        >
          <table className={`${tableDetails.xsClassName} tablenowrap`}>
            <thead>
              <tr>
                {headers.map((ele, i) => {
                  return (
                    <th key={i}>
                      <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                        {ele}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                data?.pages?.map((e, i) =>
                  e?.response?.data?.data?.map((ele) => (
                    <tr id={ele.id} key={i}>
                      <td className={tableColumns.defautCell}>
                        {ele.Channel || "WHATSAPP"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {epocToJsDate(Number(ele.timestamp)) || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.c_nm || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.country || "NA"}
                      </td>
                      {loggedInRole === "PA" && (
                        <TableCellWithTooltip>
                          {ele.accName || "NA"}
                        </TableCellWithTooltip>
                      )}

                      <td className={tableColumns.defautCell}>
                        <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                          {ele.user || "NA"}
                        </OverFlowTooltip>
                      </td>
                      {filterConfig.emailFilterEnabled && (
                        <TableCellWithTooltip>
                          {ele.userEmail || "-"}
                        </TableCellWithTooltip>
                      )}
                      {filterConfig.orgIdFilterEnabled && (
                        <TableCellWithTooltip>
                          {ele.o_id || "-"}
                        </TableCellWithTooltip>
                      )}

                      <td className={tableColumns.defautCell}>
                        {ele.i_tp === "WEB" ? t("PORTAL") : ele.i_tp || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.dir || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.wa_ba || "NA"}
                      </td>

                      <td className={tableColumns.defautCell}>
                        {ele.m_ctg || "NA"}
                      </td>
                      {/* {props?.direction !== "12" && (
                        <td className="py-4 pr-2">
                          {ele.total_sent || ele.total_sent == "0"
                            ? ele.total_sent
                            : "NA"}
                        </td>
                      )} */}
                      <td className={tableColumns.defautCell}>
                        {ele.total_sent || ele.total_sent == "0"
                          ? ele.total_sent
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.submit_success || ele.submit_success == "0"
                          ? ele.submit_success
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.dr_success || ele.dr_success == "0"
                          ? ele.dr_success
                          : "NA"}
                      </td>
                      {/* {props?.direction !== "12" && (
                        <td className="py-4 pr-2">
                          {ele.msg_read || ele.msg_read == "0"
                            ? ele.msg_read
                            : "NA"}
                        </td>
                      )} */}
                      <td className={tableColumns.defautCell}>
                        {ele.msg_read || ele.msg_read == "0"
                          ? ele.msg_read
                          : "NA"}
                      </td>

                      <td className={tableColumns.defautCell}>
                        {ele.delivery_percentage ||
                        ele.delivery_percentage == "0"
                          ? ele.delivery_percentage
                          : "NA"}
                      </td>

                      {isCBP ? (
                        <></>
                      ) : (
                        <>
                          <td className={tableColumns.defautCell}>
                            {isNumber(ele?.["free_count"])
                              ? ele?.["free_count"]
                              : "NA"}
                          </td>

                          <td className={tableColumns.defautCell}>
                            {isNumber(ele?.["paid_count"])
                              ? ele?.["paid_count"]
                              : "NA"}
                          </td>
                        </>
                      )}

                      <td className={tableColumns.defautCell}>
                        {ele.submit_failed || ele.submit_failed == "0"
                          ? ele.submit_failed
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.dr_failed || ele.dr_failed == "0"
                          ? ele.dr_failed
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.total_conv || ele.total_conv == "0"
                          ? ele.total_conv
                          : "NA"}
                      </td>
                    </tr>
                  ))
                )}
              {/* {data?.pages?.length === 1 &&
                data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                  <div>No data available</div>
                )} */}
            </tbody>
          </table>
        </InfiniteTable>
      </div>
    </>
  );
};

const PreviewSampleForSummaryForEmail = (props) => {
  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b border-gray-200",
  };
  const { loggedInRole } = props;
  const filterConfig = useReportsFiltersConfig();

  const headers = [
    t("_Date"), //time
    loggedInRole === "PA" ? t("_Account") : "", //accName
    loggedInRole === "EA" ? t("_User") : "", //user
    filterConfig.emailFilterEnabled ? t("_Email") : "",
    filterConfig.orgIdFilterEnabled ? t("_Organization_ID") : "",
    t("_Channel"), //ch_tp
    t("_Email_ID") + "'s", //s_adr
    t("_Campaign_Name"), //c_nm
    t("_Interface"), //i_tp
    t("_Total_Sent"), //total_sent
    t("_Total_Success"), //dr_success
    t("_Total_Failed"), //total_failed
    t("Success") + " (%)", //success_percentage
  ].filter((e) => e !== "");

  const { data, isFetchingNextPage, fetchNextPage, isLoading, hasNextPage } =
    props;

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <>
      <div className="w-[77vw]">
        <InfiniteTable
          dataLength={() => dataLength()}
          pageStart={0}
          initialLoad={false}
          scrollThreshold={0.5}
          next={fetchNextPage}
          hasMore={hasNextPage}
          height="56vh"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          count={allData.length}
        >
          <table className={`${tableDetails.scrollableTable} tablenowrap`}>
            <thead>
              <tr>
                {headers.map((ele, i) => {
                  return (
                    <th key={i}>
                      <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                        {ele}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                data?.pages?.map((e, i) =>
                  e?.response?.data?.data?.map((ele) => (
                    <tr id={ele.id} key={i}>
                      <td className={tableColumns.defautCell}>
                        {ele.time || "NA"}
                      </td>
                      {loggedInRole === "PA" && (
                        <TableCellWithTooltip>
                          {ele.accName || "NA"}
                        </TableCellWithTooltip>
                      )}
                      {loggedInRole === "EA" && (
                        <TableCellWithTooltip>
                          {ele.user || "NA"}
                        </TableCellWithTooltip>
                      )}
                      {filterConfig.emailFilterEnabled && (
                        <TableCellWithTooltip>
                          {ele.userEmail || "-"}
                        </TableCellWithTooltip>
                      )}
                      {filterConfig.orgIdFilterEnabled && (
                        <TableCellWithTooltip>
                          {ele.o_id || "-"}
                        </TableCellWithTooltip>
                      )}
                      <td className={tableColumns.defautCell}>
                        {ele.ch_tp || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.s_Adr || ele.s_adr || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.c_nm || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.i_tp === "WEB" ? t("PORTAL") : ele.i_tp || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.total_sent || ele.total_sent == "0"
                          ? ele.total_sent
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.submit_success || ele.submit_success == "0"
                          ? ele.submit_success
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.total_failed || ele.total_failed == "0"
                          ? ele.total_failed
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.success_percentage || ele.success_percentage == "0"
                          ? ele.success_percentage
                          : "NA"}
                      </td>
                    </tr>
                  ))
                )}
              {/* {data?.pages?.length === 1 &&
                data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                  <div>No data available</div>
                )} */}
            </tbody>
          </table>
        </InfiniteTable>
      </div>
    </>
  );
};
const PreviewSampleForDetailsForEmail = (props) => {
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    hasNextPage,
    loggedInRole,
    theme,
  } = props;

  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b border-gray-200",
  };

  const unfilteredHeaders = [
    loggedInRole === "PA" ? t("_Account") : "", //accName
    loggedInRole !== "EU" ? t("_User") : "", //user
    //loggedInRole !== "EU" ? "User Email" : "", //user email
    t("_Transaction_ID"), //Message Id
    t("_Accepted_Time"), //Submit Time
    t("_Direction"),
    t("_Channel"), //Channel type
    t("_Sender"), //sender_address
    t("E-Mail_ID_of_Account"),
    //"Content Type", //content type
    t("_Interface"), //Interface type
    t("_Category"), //Campaign category
    t("_AuthConfig_Profile"), //AuthConfig Profile
    t("_Campaign_Type"), //Job type
    t("_Name"), //Campaign Name
    t("_Campaign_ID"), //Parent job id
    t("_Type"), //Message template
    //"Fragment Count", //Fragment Count
    t("_Name"), //package_name
    t("_Package_Units"), //package_unit
    t("_Rate_Sheet"), //ratesheetname
    t("Rate_Sheet_Per_Unit_Price"), //rate_sheet_per_unit
    t("Total_Price"), //total_price
    // loggedInRole === "PA" ? "Provider" : "", //Provider
    // loggedInRole === "PA" ? "Bind" : "", //Bind
    // loggedInRole === "PA" ? "Unit Cost" : "", //unit_cost
    // loggedInRole === "PA" ? "Total Cost" : "", //total_cost
    t("Submitted_Time"), //submitted_time
    // "Delivery Time", //delivered_time
    t("Delivery_Status"), //status
    t("Status_Code"),
    t("Failure_Reason"),
  ];

  const headers = unfilteredHeaders.filter((e) => e !== "");

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <div className="w-[77vw]">
      <InfiniteTable
        dataLength={() => dataLength()}
        pageStart={0}
        initialLoad={false}
        height="56vh"
        scrollThreshold={0.5}
        next={fetchNextPage}
        hasMore={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        count={allData.length}
      >
        <table className={`${tableDetails.xsClassName} tablenowrap`}>
          <thead>
            <tr>
              {headers.map((ele, i) => {
                return (
                  <th key={i}>
                    <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                      {ele}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="overflow-x-auto">
            {!isLoading &&
              data?.pages?.map((e, i) =>
                e?.response?.data?.data?.map((ele) => (
                  <tr id={ele.id} key={i}>
                    {loggedInRole === "PA" && (
                      <td className={tableColumns.defautCell}>
                        <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                          {ele.accName || "NA"}
                        </OverFlowTooltip>
                      </td>
                    )}
                    {loggedInRole !== "EU" && (
                      <td className={tableColumns.defautCell}>
                        <OverFlowTooltip style={{ maxWidth: nameMaxWidth }}>
                          {ele.user || "NA"}
                        </OverFlowTooltip>
                      </td>
                    )}
                    <td className={tableColumns.defautCell}>
                      {ele["Message Id"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {getLocalFormattedDate(
                        ele["Submit Time"] || "NA",
                        theme.dateFormat,
                        theme.timeFormat
                      )}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Direction"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Channel type"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Sender"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {" "}
                      {ele["E-Mail ID of Account"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Interface type"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Campaign Category"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["AuthConfig Profile"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Job Type"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Campaign Name"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Parent job id"] || "NA"}
                    </td>

                    <td className={tableColumns.defautCell}>
                      {ele["Message Type"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Package Name"]?.split(":")?.[0] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Package Units"] || ele["Package Units"] == "0"
                        ? ele["Package Units"]
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Rate Sheet Name"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["rate_sheet_per_unit"] ||
                      ele["rate_sheet_per_unit"] == "0"
                        ? ele["rate_sheet_per_unit"]
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Total Price"] || ele["Total Price"] == "0"
                        ? ele["Total Price"]
                        : "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {getLocalFormattedDate(
                        ele["Submit Time"] || "NA",
                        theme.dateFormat,
                        theme.timeFormat
                      )}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Delivery Status"] || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele.Status || "NA"}
                    </td>
                    <td className={tableColumns.defautCell}>
                      {ele["Failure Reason"] || "NA"}
                    </td>
                  </tr>
                ))
              )}
            {/* {data?.pages?.length === 1 &&
                data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                  <div>No data available</div>
                )} */}
          </tbody>
        </table>
      </InfiniteTable>
    </div>
  );
};
const PreviewSampleForSummaryForRCS = (props) => {
  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "  lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b   border-gray-200",
  };
  const { loggedInRole } = props;
  const headers = [
    "Date", //time
    loggedInRole === "PA" ? t("_Account") : "", //accName
    loggedInRole === "EA" ? "User" : "", //user
    "Channel", //ch_tp
    "Email ID's", //s_adr
    "Interface", //i_tp
    "Total Sent", //total_sent
    "Total Success", //dr_success
    "Total Failed", //total_failed
    "Success (%)", //success_percentage
  ].filter((e) => e !== "");

  const { data, isFetchingNextPage, fetchNextPage, isLoading, hasNextPage } =
    props;

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <>
      <div className="w-[77vw]">
        <InfiniteTable
          dataLength={() => dataLength()}
          pageStart={0}
          initialLoad={false}
          scrollThreshold={0.5}
          next={fetchNextPage}
          hasMore={hasNextPage}
          height="56vh"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          count={allData.length}
        >
          <table className={`${tableDetails.scrollableTable} tablenowrap`}>
            <thead>
              <tr>
                {headers.map((ele, i) => {
                  return (
                    <th key={i}>
                      <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                        {ele}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="overflow-x-auto">
              {!isLoading &&
                data?.pages?.map((e, i) =>
                  e?.response?.data?.data?.map((ele) => (
                    <tr id={ele.id} key={i}>
                      <td className={tableColumns.defautCell}>
                        {ele.time || "NA"}
                      </td>
                      {loggedInRole === "PA" && (
                        <TableCellWithTooltip>
                          {ele.accName || "NA"}
                        </TableCellWithTooltip>
                      )}
                      {loggedInRole === "EA" && (
                        <TableCellWithTooltip>
                          {ele.user || "NA"}
                        </TableCellWithTooltip>
                      )}
                      <td className={tableColumns.defautCell}>
                        {ele.ch_tp || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.s_ADR || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.i_tp === "WEB" ? t("PORTAL") : ele.i_tp || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.total_sent || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.submit_success || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.total_failed || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele.success_percentage || "NA"}
                      </td>
                    </tr>
                  ))
                )}
              {/* {data?.pages?.length === 1 &&
                data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                  <div>No data available</div>
                )} */}
            </tbody>
          </table>
        </InfiniteTable>
      </div>
    </>
  );
};
const PreviewSampleForDetailsForRCS = (props) => {
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    hasNextPage,
    loggedInRole,
  } = props;
  const { theme } = useTheme();

  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b border-gray-200",
  };

  const unfilteredHeaders = [
    loggedInRole === "PA" ? t("_Account") : "", //Account
    loggedInRole !== "EU" ? "User" : "", //User
    "Transaction ID", //Transaction ID
    "Accepted Time", //Accepted Time
    "Sender/Number", //Sender
    "Country", //Country
    "MSISDN", //MSISDN
    "Interface", //Interface type
    "Campaign Category", //Category
    "Message Type", //Message template
    "Campaign ID", //Campaign ID
    "Submitted Time", //Submitted Time
    "Delivered Time", //Delivery Time
    "Status Code", //Status Code
    "Status", //Status
    "Failure Reason", //Failure Reason
  ];

  const headers = unfilteredHeaders.filter((e) => e !== "");

  const dataLength = () => {
    let length = tableDetails.pageSize;

    data?.pages?.forEach((ele) => {
      length = length + ele?.response?.data?.data?.length;
    });

    return length;
  };

  const allData = useMemo(
    () =>
      data?.pages?.flatMap((page) => page?.response?.data?.data ?? []) ?? [],
    [data]
  );

  return (
    <div className="w-[77vw]">
      {!isLoading && (
        <InfiniteTable
          dataLength={() => dataLength()}
          pageStart={0}
          initialLoad={false}
          height="56vh"
          scrollThreshold={0.5}
          next={fetchNextPage}
          hasMore={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          count={allData.length}
        >
          <table className={`${tableDetails.xsClassName} tablenowrap`}>
            <thead>
              <tr>
                {headers.map((ele, i) => {
                  return (
                    <th key={i}>
                      <div className="flex flex-row gap-1 place-items-center whitespace-nowrap">
                        {ele}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="overflow-x-auto">
              {!isLoading &&
                data?.pages?.map((e, i) =>
                  e?.response?.data?.data?.map((ele) => (
                    <tr id={ele.id} key={i}>
                      {loggedInRole === "PA" && (
                        <TableCellWithTooltip>
                          {ele.Account || "NA"}
                        </TableCellWithTooltip>
                      )}
                      {loggedInRole !== "EU" && (
                        <TableCellWithTooltip>
                          {ele.User || "NA"}
                        </TableCellWithTooltip>
                      )}
                      <td className={tableColumns.defautCell}>
                        {ele["Transaction ID"] || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Accepted Time"] && !isNaN(ele["Accepted Time"])
                          ? dayjs(
                              new Date(parseInt(ele["Accepted Time"]))
                            ).format(theme.dateFormat + " " + theme.timeFormat)
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Sender"] || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Country"] || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["MSISDN"] || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Interface type"] || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Category"] || "NA"}
                      </td>

                      <td className={tableColumns.defautCell}>
                        {ele["Message Type"] || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Campaign ID"]?.split(":")?.[0] || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Submitted Time"] && !isNaN(ele["Submitted Time"])
                          ? dayjs(
                              new Date(parseInt(ele["Submitted Time"]))
                            ).format(theme.dateFormat + " " + theme.timeFormat)
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Delivery Time"] && !isNaN(ele["Delivery Time"])
                          ? dayjs(
                              new Date(parseInt(ele["Delivery Time"]))
                            ).format(theme.dateFormat + " " + theme.timeFormat)
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Status Code"] || ele["Status Code"] == "0"
                          ? ele["Status Code"]
                          : "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Status"] || "NA"}
                      </td>
                      <td className={tableColumns.defautCell}>
                        {ele["Failure Reason"] || "NA"}
                      </td>
                    </tr>
                  ))
                )}
              {/* {data?.pages?.length === 1 &&
                data?.pages?.[0]?.response?.data?.data?.length === 0 && (
                  <div>No data available</div>
                )} */}
            </tbody>
          </table>
        </InfiniteTable>
      )}
    </div>
  );
};

const InputFieldComponent = ({ name, label, placeholder }) => {
  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b border-gray-200",
  };
  return (
    <div>
      <label className={styles.labelStyles}>{label}</label>
      <Field
        type="text"
        placeholder={placeholder}
        name={name}
        className="bg-transparent w-full h-11 text-sm placeholder-[#CACACA] bg-clip-padding border border-solid border-gray-300 rounded  focus:border-blue-600 focus:outline-none text-black font-normal"
      />
      <ErrorMessage
        name={name}
        component="div"
        className="text-[#FF6E6E] text-xs"
      />
    </div>
  );
};

const CommonReports = (props) => {
  let styles = {
    labelStyles: "text-sm font-bold text-Dove-Gray",
    tableStyle: "table bg-white border-b w-full divide-y divide-gray-200 z-0",
    rowStyle:
      "lg:text-black cursor-pointer text-sm font-semibold w:bg-gray-100 hover:bg-gray-100 border-b border-gray-200",
  };

  const filterConfig = useReportsFiltersConfig();

  const [isButtonClicked, setIsButtonClicked] = useState("");
  // Optional customer supplied report/file name, collected just before download.
  const [showDownloadNameModal, setShowDownloadNameModal] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [updatedValues, setUpdatedValues] = useState({
    ...props.dropdownValues,
  });
  const [isDetailQueryEnabled, setIsDetailQueryEnabled] = useState(false);
  const [isVoiceDetailQueryEnabled, setIsVoiceDetailQueryEnabled] =
    useState(false);
  const [isEmailDetailQueryEnabled, setIsEmailDetailQueryEnabled] =
    useState(false);
  const [isWADetailQueryEnabled, setIsWADetailQueryEnabled] = useState(false);
  const [bindOptions, setBindOptions] = useState([]);
  const [isSummaryQueryEnabled, setIsSummaryQueryEnabled] = useState(false);
  const [isVoiceSummaryQueryEnabled, setIsVoiceSummaryQueryEnabled] =
    useState(false);
  const [isEmailSummaryQueryEnabled, setIsEmailSummaryQueryEnabled] =
    useState(false);
  const [isWASummaryQueryEnabled, setIsWASummaryQueryEnabled] = useState(false);
  const [emailOptions, setEmailOptions] = useState([]);
  const [isRefreshDisabled, setIsRefreshDisabled] = useState(true);
  const [
    isOfflineDetailedReportTableEnabled,
    setIsOfflineDetailedReportTableEnabled,
  ] = useState(false);
  const [
    isOfflineSummaryReportTableEnabled,
    setIsOfflineSummaryReportTableEnabled,
  ] = useState(false);
  const [
    isOfflineVoiceDetailedReportTableEnabled,
    setIsOfflineVoiceDetailedReportTableEnabled,
  ] = useState(false);
  const [
    isOfflineVoiceSummaryReportTableEnabled,
    setIsOfflineVoiceSummaryReportTableEnabled,
  ] = useState(false);
  const [
    isOfflineEmailDetailedReportTableEnabled,
    setIsOfflineEmailDetailedReportTableEnabled,
  ] = useState(false);
  const [
    isOfflineEmailSummaryReportTableEnabled,
    setIsOfflineEmailSummaryReportTableEnabled,
  ] = useState(false);
  const [
    isOfflineWADetailedReportTableEnabled,
    setIsOfflineWADetailedReportTableEnabled,
  ] = useState(false);
  const [
    isOfflineWASummaryReportTableEnabled,
    setIsOfflineWASummaryReportTableEnabled,
  ] = useState(false);

  const [openInfoModal, setOpenInfoModal] = useState(false);
  const { theme } = useTheme();
  const {
    type: reportType,
    loggedInRole,
    isPreviewSampleClicked,
    setIsPreviewSampleClicked,
    isGenerateReportClicked,
    setIsGenerateReportClicked,
    globalConfig,
  } = props;

  let initialValues = { ...props.dropdownValues };

  const DisplayValidationSchema = Yup.object().shape({
    // dateRange: Yup.array(
    //   Yup.string()
    //     .required("Start and End date is required")
    //     .typeError("Start and End date is required")
    // ),
    channel: Yup.string().required(t("_Channel_is_required")),
    productType: Yup.string().when("channel", {
      is: (channel) => channel === "voice",
      then: Yup.string().required("Product Type is required"),
    }),
    msisdn: Yup.string()
      .matches(/^[0-9]*$/, "Enter valid MSISDN")
      .min(6, "MSISDN must be at least 6 numbers")
      .max(15, "MSISDN must be at most 15 numbers"),
    startDate: Yup.string().required("Start Date is required"),
    endDate: Yup.string().required("End Date is required"),
    campaignId: Yup.string()
      .uuid("Please enter valid UUID")
      .typeError("Please enter valid UUID"),
    emailid: Yup.string()
      .matches(
        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/,
        t("_Invalid_Email_Format")
      )
      .min(5, t("_Minimum_Length_is_5"))
      .max(50, t("_Max_length_allowed_is_50")),
    userEmail: Yup.string()
      .matches(
        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/,
        t("_Invalid_Email_Format")
      )
      .min(5, t("_Minimum_Length_is_5"))
      .max(50, t("_Max_length_allowed_is_50"))
      .optional(),
    organizationId: Yup.string()
      .uuid("Please enter valid UUID")
      .typeError("Please enter valid UUID")
      .optional(),
  });
  const enableAuthConfig = useIsAuthConfigEnabled();
  const reportNamePromptEnabled = useIsReportNamePromptEnabled();
  const { userDetails, allCountries, allowedChannels } = useAuthContext();

  let channelOpts = channelOptions.filter(
    (e) => allowedChannels.indexOf(e.label) > -1
  );

  useEffect(() => {
    setUpdatedValues({ ...props.dropdownValues });
    if (reportType === "detailed") setIsOfflineDetailedReportTableEnabled(true);
    else {
      setIsOfflineSummaryReportTableEnabled(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    hasNextPage: summaryHasNextPage,
    fetchNextPage: mtSummaryViewFetchNextPage,
    isFetchingNextPage: summaryIsFetchingNextPage,
  } = useInfiniteQuery(
    [
      "MtSummaryView",
      userDetails.token,
      updatedValues,
      "csv",
      "MT_SUMMARY",
      loggedInRole,
    ],
    getReports,
    {
      enabled: isSummaryQueryEnabled,
      getNextPageParam: (lastPage) => {
        if (lastPage?.response?.data?.data?.length === tableDetails.pageSize) {
          return lastPage.nextPage;
        } else {
          return undefined;
        }
      },
      onError: (error) => {
        // setErrorModal(true);
        setIsSummaryQueryEnabled(false);
        setErrorMsg(error?.response?.data || error?.response?.data?.message);
      },
      onSuccess: (data) => {
        setIsSummaryQueryEnabled(false);
      },
    }
  );

  const {
    data: detailsData,
    isLoading: isDetailsLoading,
    hasNextPage: detailsHasNextPage,
    fetchNextPage: detailsFetchNextPage,
    isFetchingNextPage: detailsIsFetchingNextPage,
  } = useInfiniteQuery(
    [
      "MtDetailView",
      userDetails.token,
      updatedValues,
      "csv",
      "MT_DETAIL",
      loggedInRole,
    ],
    getReports,
    {
      enabled: isDetailQueryEnabled,
      getNextPageParam: (lastPage) => {
        if (lastPage?.response?.data?.data?.length === tableDetails.pageSize) {
          return lastPage.nextPage;
        } else {
          return undefined;
        }
      },
      onError: (error) => {
        setIsDetailQueryEnabled(false);
        setErrorMsg(error?.response?.data || error?.response?.data?.message);
      },
      onSuccess: (data) => {
        setIsDetailQueryEnabled(false);
      },
    }
  );

  const {
    data: voicedetailsData,
    isLoading: isVoiceDetailsLoading,
    hasNextPage: voicedetailsHasNextPage,
    fetchNextPage: voicedetailsFetchNextPage,
    isFetchingNextPage: voicedetailsIsFetchingNextPage,
  } = useInfiniteQuery(
    [
      "VoiceDetailView",
      userDetails.token,
      updatedValues,
      "csv",
      props.type,
      loggedInRole,
    ],
    getVoiceReports,
    {
      enabled: isVoiceDetailQueryEnabled,
      getNextPageParam: (lastPage) => {
        if (lastPage?.response?.data?.data?.length === tableDetails.pageSize) {
          return lastPage.nextPage;
        } else {
          return undefined;
        }
      },
      onError: (error) => {
        console.log("api error vd: ", error.response);
        setIsVoiceDetailQueryEnabled(false);
        setErrorMsg(error?.response?.data || error?.response?.data?.message);
      },
      onSuccess: (data) => {
        setIsVoiceDetailQueryEnabled(false);
      },
    }
  );
  const {
    data: voiceSummaryData,
    isLoading: isVoiceSummaryLoading,
    hasNextPage: voiceSummaryHasNextPage,
    fetchNextPage: voiceSummaryFetchNextPage,
    isFetchingNextPage: voiceSummaryIsFetchingNextPage,
  } = useInfiniteQuery(
    [
      "VoiceSummaryView",
      userDetails.token,
      updatedValues,
      "csv",
      props.type,
      loggedInRole,
    ],
    getVoiceReports,
    {
      enabled: isVoiceSummaryQueryEnabled,
      getNextPageParam: (lastPage) => {
        if (lastPage?.response?.data?.data?.length === tableDetails.pageSize) {
          return lastPage.nextPage;
        } else {
          return undefined;
        }
      },
      onError: (error) => {
        console.log("api error vd: ", error?.response);
        setIsVoiceSummaryQueryEnabled(false);
        setErrorMsg(error?.response?.data || error?.response?.data?.message);
      },
      onSuccess: (data) => {
        setIsVoiceSummaryQueryEnabled(false);
      },
    }
  );

  const {
    data: emailSummaryData,
    isLoading: isEmailSummaryLoading,
    hasNextPage: emailSummaryHasNextPage,
    fetchNextPage: emailSummaryFetchNextPage,
    isFetchingNextPage: emailSummaryIsFetchingNextPage,
  } = useInfiniteQuery(
    [
      "EMAILSummaryView",
      userDetails.token,
      updatedValues,
      "csv",
      "summary",
      loggedInRole,
    ],
    getEMAILReports,
    {
      enabled: isEmailSummaryQueryEnabled,
      getNextPageParam: (lastPage) => {
        if (lastPage?.response?.data?.data?.length === tableDetails.pageSize) {
          return lastPage.nextPage;
        } else {
          return undefined;
        }
      },
      onError: (error) => {
        console.log("api error vd: ", error?.response);
        setIsEmailSummaryQueryEnabled(false);
        setErrorMsg(error?.response?.data || error?.response?.data?.message);
      },
      onSuccess: (data) => {
        setIsEmailSummaryQueryEnabled(false);
      },
    }
  );
  const {
    data: emailDetailsData,
    isLoading: isEmailDetailsLoading,
    hasNextPage: emailDetailsHasNextPage,
    fetchNextPage: emailDetailsFetchNextPage,
    isFetchingNextPage: emailDetailsIsFetchingNextPage,
  } = useInfiniteQuery(
    [
      "EmailDetailsView",
      userDetails.token,
      updatedValues,
      "csv",
      "ALL_DETAIL_EMAIL",
      loggedInRole,
    ],
    getEMAILReports,
    {
      enabled: isEmailDetailQueryEnabled,
      getNextPageParam: (lastPage) => {
        if (lastPage?.response?.data?.data?.length === tableDetails.pageSize) {
          return lastPage.nextPage;
        } else {
          return undefined;
        }
      },
      onError: (error) => {
        setIsEmailDetailQueryEnabled(false);
        setErrorMsg(error?.response?.data || error?.response?.data?.message);
      },
      onSuccess: () => {
        setIsEmailDetailQueryEnabled(false);
      },
    }
  );
  const {
    data: waSummaryData,
    isLoading: isWaSummaryLoading,
    hasNextPage: waSummaryHasNextPage,
    fetchNextPage: waSummaryViewFetchNextPage,
    isFetchingNextPage: waSummaryIsFetchingNextPage,
  } = useInfiniteQuery(
    [
      "WASummaryView",
      userDetails,
      updatedValues,
      "csv",
      "WA_SUMMARY",
      loggedInRole,
    ],
    getWAReports,
    {
      enabled: isWASummaryQueryEnabled,
      getNextPageParam: (lastPage) => {
        if (lastPage?.response?.data?.data?.length === tableDetails.pageSize) {
          return undefined;
        } else {
          return undefined;
        }
      },
      onError: (error) => {
        // setErrorModal(true);
        setIsWASummaryQueryEnabled(false);
        setErrorMsg(error?.response?.data || error?.response?.data?.message);
      },
      onSuccess: (data) => {
        setIsWASummaryQueryEnabled(false);
      },
    }
  );

  const {
    data: waDetailsData,
    isLoading: isWaDetailsLoading,
    hasNextPage: waDetailsHasNextPage,
    fetchNextPage: waDetailsFetchNextPage,
    isFetchingNextPage: waDetailsIsFetchingNextPage,
  } = useInfiniteQuery(
    [
      "WADetailView",
      userDetails,
      updatedValues,
      "csv",
      "ALL_DETAIL_WA",
      loggedInRole,
    ],
    getWAReports,
    {
      enabled: isWADetailQueryEnabled,
      getNextPageParam: (lastPage) => {
        if (lastPage?.response?.data?.data?.length === tableDetails.pageSize) {
          return lastPage.nextPage;
        } else {
          return undefined;
        }
      },
      onError: (error) => {
        // setErrorModal(true);
        setIsWADetailQueryEnabled(false);
        setErrorMsg(error?.response?.data || error?.response?.data?.message);
      },
      onSuccess: (data) => {
        setIsWADetailQueryEnabled(false);
      },
    }
  );

  const {
    data: offlineDetailedReportTableData,
    isLoading: isOfflineDetailedReportTableDataLoading,
  } = useQuery(
    [
      "MtDetailDownload_OfflineReports",
      userDetails.token,
      {
        ...updatedValues,
        startDate:
          dayjs(new Date()).subtract(29, "d").format("YYYY-MM-DD") +
          "T00:00:00.000" +
          "Z",
        endDate: dayjs(new Date()).format("YYYY-MM-DD") + "T00:00:00.000" + "Z",
      },
      "DETAILED",
    ],
    getOfflineReports,
    {
      enabled: isOfflineDetailedReportTableEnabled,
      onError: (err) => {
        console.log(
          "error while fetching offline detail report table data: ",
          err,
          err?.response
        );
      },
      onSuccess: (data) => {
        setIsRefreshDisabled(false);
      },
      onSettled: (data) => {
        setIsOfflineDetailedReportTableEnabled(false);
      },
    }
  );

  const {
    data: offlineSummaryReportTableData,
    isLoading: isOfflineSummaryReportTableDataLoading,
  } = useQuery(
    [
      "MtSummaryDownload_OfflineReports",
      userDetails.token,
      {
        ...updatedValues,
        startDate:
          dayjs(new Date()).subtract(29, "d").format("YYYY-MM-DD") +
          "T00:00:00.000" +
          "Z",
        endDate: dayjs(new Date()).format("YYYY-MM-DD") + "T00:00:00.000" + "Z",
      },
      "SUMMARY",
    ],
    getOfflineReports,
    {
      enabled: isOfflineSummaryReportTableEnabled,
      onError: (err) => {
        console.log(
          "error while fetching offline summary report table data: ",
          err,
          err?.response
        );
      },
      onSuccess: (data) => {
        setIsRefreshDisabled(false);
      },
      onSettled: (data) => {
        setIsOfflineSummaryReportTableEnabled(false);
      },
    }
  );

  const {
    data: offlineEmailDetailedReportTableData,
    isLoading: isOfflineEmailDetailedReportTableDataLoading,
  } = useQuery(
    [
      "MtEmailDetailDownload_OfflineReports",
      userDetails.token,
      {
        ...updatedValues,
        startDate:
          dayjs(new Date()).subtract(29, "d").format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
        endDate:
          dayjs(new Date()).format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
      },
      "DETAILED",
    ],
    getEmailOfflineReports,
    {
      enabled: isOfflineEmailDetailedReportTableEnabled,
      onError: (err) => {
        console.log(
          "error while fetching offline detail report table data: ",
          err,
          err?.response
        );
      },
      onSuccess: (data) => {
        setIsRefreshDisabled(false);
      },
      onSettled: (data) => {
        setIsOfflineEmailDetailedReportTableEnabled(false);
      },
    }
  );

  const {
    data: offlineEmailSummaryReportTableData,
    isLoading: isOfflineEmailSummaryReportTableDataLoading,
  } = useQuery(
    [
      "MtEmailSummaryDownload_OfflineReports",
      userDetails.token,
      {
        ...updatedValues,
        startDate:
          dayjs(new Date()).subtract(29, "d").format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
        endDate:
          dayjs(new Date()).format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
      },
      "SUMMARY",
    ],
    getEmailOfflineReports,
    {
      enabled: isOfflineEmailSummaryReportTableEnabled,
      onError: (err) => {
        console.log(
          "error while fetching offline summary report table data: ",
          err,
          err?.response
        );
      },
      onSuccess: (data) => {
        setIsRefreshDisabled(false);
      },
      onSettled: (data) => {
        setIsOfflineEmailSummaryReportTableEnabled(false);
      },
    }
  );

  const {
    data: offlineVoiceDetailedReportTableData,
    isLoading: isOfflineVoiceDetailedReportTableDataLoading,
  } = useQuery(
    [
      "VoiceDetailDownload_OfflineReports",
      userDetails.token,
      {
        ...updatedValues,
        startDate:
          dayjs(new Date()).subtract(29, "d").format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
        endDate:
          dayjs(new Date()).format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
      },
      "DETAILED",
    ],
    getOfflineVoiceReports,
    {
      enabled: isOfflineVoiceDetailedReportTableEnabled,
      onError: (err) => {
        console.log(
          "error while fetching offline detail report table data: ",
          err,
          err?.response
        );
      },
      onSuccess: (data) => {
        setIsRefreshDisabled(false);
      },
      onSettled: (data) => {
        setIsOfflineVoiceDetailedReportTableEnabled(false);
      },
    }
  );
  const {
    data: offlineVoiceSummaryReportTableData,
    isLoading: isOfflineVoiceSummaryReportTableDataLoading,
  } = useQuery(
    [
      "VoiceSummaryDownload_OfflineReports",
      userDetails.token,
      {
        ...updatedValues,
        startDate:
          dayjs(new Date()).subtract(29, "d").format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
        endDate:
          dayjs(new Date()).format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
      },
      "SUMMARY",
    ],
    getOfflineVoiceReports,
    {
      enabled: isOfflineVoiceSummaryReportTableEnabled,
      onError: (err) => {
        console.log(
          "error while fetching offline summary report table data: ",
          err,
          err?.response
        );
      },
      onSuccess: (data) => {
        setIsRefreshDisabled(false);
      },
      onSettled: (data) => {
        setIsOfflineVoiceSummaryReportTableEnabled(false);
      },
    }
  );

  const {
    data: offlineWaDetailedReportTableData,
    isLoading: isOfflineWaDetailedReportTableDataLoading,
  } = useQuery(
    [
      "WADetailDownload_OfflineReports",
      userDetails,
      {
        ...updatedValues,
        startDate:
          dayjs(new Date()).subtract(29, "d").format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
        endDate:
          dayjs(new Date()).format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
      },
      "DETAILED",
      loggedInRole,
    ],
    getWAOfflineReports,
    {
      enabled: isOfflineWADetailedReportTableEnabled,
      onError: (err) => {
        console.log(
          "error while fetching offline detail report table data: ",
          err,
          err?.response
        );
      },
      onSuccess: (data) => {
        setIsRefreshDisabled(false);
      },
      onSettled: (data) => {
        setIsOfflineWADetailedReportTableEnabled(false);
      },
    }
  );

  const {
    data: offlineWaSummaryReportTableData,
    isLoading: isOfflineWaSummaryReportTableDataLoading,
  } = useQuery(
    [
      "WASummaryDownload_OfflineReports",
      userDetails,
      {
        ...updatedValues,
        startDate:
          dayjs(new Date()).subtract(29, "d").format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
        endDate:
          dayjs(new Date()).format("YYYY-MM-DD") +
          "T00:00:00.000" +
          getTimeZoneInHours(),
      },
      "SUMMARY",
      loggedInRole,
    ],
    getWAOfflineReports,
    {
      enabled: isOfflineWASummaryReportTableEnabled,
      onError: (err) => {
        console.log(
          "error while fetching offline summary report table data: ",
          err,
          err?.response
        );
      },
      onSuccess: (data) => {
        setIsRefreshDisabled(false);
      },
      onSettled: (data) => {
        setIsOfflineWASummaryReportTableEnabled(false);
      },
    }
  );

  const getBindOptions = async () => {
    let sp_id = props.dropdownValues.provider.value;
    const { channel } = props.dropdownValues;
    let providerData = {};
    if (sp_id && channel) {
      try {
        providerData = await getProviderForBind(
          userDetails.token,
          sp_id,
          channel,
          t("Reports")
        );

        return providerData;
      } catch (err) {
        console.log("error: ", err);
      }
    } else {
      return [];
    }
  };

  let countryOptions = [];
  allCountries?.forEach((country) => {
    countryOptions.push({
      label: country.c_name,
      value: country.c_code_2,
    });
  });

  countryOptions = [...[{ label: "All", value: "all" }], ...countryOptions];

  useEffect(() => {
    getBindOptions().then((res) => {
      setBindOptions(res);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dropdownValues.provider, props.dropdownValues.channel]);

  useEffect(() => {
    let dummyEmailOptions = [];
    setEmailOptions(dummyEmailOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSummaryReport = props.type === reportTypes.SUMMARY;
  const isDetailedReport = props.type === reportTypes.DETAILED;

  return (
    <div className="mt-5">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={DisplayValidationSchema}
        validateOnMount
        validateOnChange
        validate={(values) => {
          props.setDropdownValues({ ...values });
        }}
        onSubmit={(values) => {
          const provider = values.provider.label;
          values.provider = provider;
          const user = values.user.value;
          values.user = user;
          const sender = values.sender.value;
          values.sender = sender;
          const whatsAppnumber = values?.whatsAppnumber?.value;
          values.whatsAppnumber = whatsAppnumber;

          const packageName = values.packageName?.label;
          values.packageName = packageName;
          const packageType = values.packageType?.label;
          values.packageType = packageType;
          const rateSheetName = values.rateSheetName?.label;
          values.rateSheetName = rateSheetName;
          const callerId = values.callerId?.value;
          values.callerId = callerId;

          const campNames = values.campNames?.map((e) => e.value)?.join(",");
          values.campNames = campNames;

          const temp_id = values.temp_id?.map((e) => e.value)?.join(",");
          values.temp_id = temp_id;

          setUpdatedValues(values);
          if (isButtonClicked === "PreviewSample") {
            if (isSummaryReport) {
              if (values.channel === "voice") {
                setIsVoiceSummaryQueryEnabled(true);
              } else if (values?.channel === "email") {
                setIsEmailSummaryQueryEnabled(true);
              } else if (values?.channel === "wa") {
                setIsWASummaryQueryEnabled(true);
              } else {
                setIsSummaryQueryEnabled(true);
              }
            } else if (isDetailedReport) {
              if (values.channel === "voice") {
                setIsVoiceDetailQueryEnabled(true);
              } else if (values?.channel === "email") {
                setIsEmailDetailQueryEnabled(true);
              } else if (values?.channel === "wa") {
                setIsWADetailQueryEnabled(true);
              } else {
                setIsDetailQueryEnabled(true);
              }
            }
            setIsPreviewSampleClicked(true);
            setIsGenerateReportClicked(false);
          } else {
            if (isSummaryReport) {
              if (values.channel === "voice") {
                getVoiceReports({
                  queryKey: [
                    "VoiceSummaryDownload",
                    userDetails.token,
                    values,
                    "csv",
                    props.type,
                    loggedInRole,
                  ],
                })
                  .then(() => {})
                  .catch((err) => {
                    console.log(
                      "error while trying to call download report: ",
                      err?.response?.data?.msg
                    );
                    setErrorMsg(err?.response?.msg || err?.response?.data?.msg);
                    setErrorModal(true);
                  })
                  .finally(() => {
                    setIsOfflineVoiceSummaryReportTableEnabled(true);
                  });
              } else if (values?.channel === "email") {
                getEMAILReports({
                  queryKey: [
                    "EMAILSummaryDownload",
                    userDetails.token,
                    values,
                    "csv",
                    "EMAIL_SUMMARY",
                    loggedInRole,
                  ],
                })
                  .then(() => {})
                  .catch((err) => {
                    console.log(
                      "error while trying to call download report: ",
                      err?.response?.data?.msg
                    );
                    setErrorMsg(err?.response?.msg || err?.response?.data?.msg);
                    setErrorModal(true);
                  })
                  .finally(() => {
                    setIsOfflineEmailSummaryReportTableEnabled(true);
                  });
              } else if (values?.channel === "wa") {
                getWAReports({
                  queryKey: [
                    "WASummaryDownload",
                    userDetails,
                    {
                      ...values,
                      endDate: values.endDate.includes(
                        dayjs(new Date()).format("YYYY-MM-DD")
                      )
                        ? dayjs(new Date()).format("YYYY-MM-DD") +
                          `T${
                            dayjs().format("YYYY-MM-DD HH:mm:ss").split(" ")[1]
                          }.000` +
                          getTimeZoneInHours()
                        : values.endDate,
                    },
                    "csv",
                    "WA_SUMMARY",
                    loggedInRole,
                  ],
                })
                  .then(() => {})
                  .catch((err) => {
                    console.log(
                      "error while trying to call download report: ",
                      err?.response?.data?.msg
                    );
                    setErrorMsg(err?.response?.msg || err?.response?.data?.msg);
                    setErrorModal(true);
                  })
                  .finally(() => {
                    setIsOfflineWASummaryReportTableEnabled(true);
                  });
              } else {
                getReports({
                  queryKey: [
                    "MtSummaryDownload",
                    userDetails.token,
                    values,
                    "csv",
                    "MT_SUMMARY",
                    loggedInRole,
                  ],
                })
                  .then(() => {})
                  .catch((err) => {
                    console.log(
                      "error while trying to call download report: ",
                      err?.response?.data?.msg
                    );
                    setErrorMsg(err?.response?.msg || err?.response?.data?.msg);
                    setErrorModal(true);
                  })
                  .finally(() => {
                    setIsOfflineSummaryReportTableEnabled(true);
                  });
              }
            } else if (isDetailedReport) {
              if (values.channel === "voice") {
                getVoiceReports({
                  queryKey: [
                    "VoiceDetailDownload",
                    userDetails.token,
                    values,
                    "csv",
                    props.type,
                    loggedInRole,
                  ],
                })
                  .then(() => {})
                  .catch((err) => {
                    console.log(
                      "error while trying to call download report: ",
                      err?.response?.data?.msg
                    );
                    setErrorMsg(err?.response?.msg || err?.response?.data?.msg);
                    setErrorModal(true);
                  })
                  .finally(() => {
                    setIsOfflineVoiceDetailedReportTableEnabled(true);
                  });
              } else if (values?.channel === "email") {
                getEMAILReports({
                  queryKey: [
                    "EmailDetailDownload",
                    userDetails.token,
                    values,
                    "csv",
                    "ALL_DETAIL_EMAIL",
                    loggedInRole,
                  ],
                })
                  .then(() => {})
                  .catch((err) => {
                    console.log(
                      "error while trying to call download report: ",
                      err?.response?.data?.msg
                    );
                    setErrorMsg(err?.response?.msg || err?.response?.data?.msg);
                    setErrorModal(true);
                  })
                  .finally(() => {
                    setIsOfflineEmailDetailedReportTableEnabled(true);
                  });
              } else if (values?.channel === "wa") {
                getWAReports({
                  queryKey: [
                    "WADetailDownload",
                    userDetails,
                    {
                      ...values,
                      endDate: values.endDate.includes(
                        dayjs(new Date()).format("YYYY-MM-DD")
                      )
                        ? dayjs(new Date()).format("YYYY-MM-DD") +
                          `T${
                            dayjs().format("YYYY-MM-DD HH:mm:ss").split(" ")[1]
                          }.000` +
                          getTimeZoneInHours()
                        : values.endDate,
                    },
                    "csv",
                    "ALL_DETAIL_WA",
                    loggedInRole,
                  ],
                })
                  .then(() => {})
                  .catch((err) => {
                    console.log(
                      "error while trying to call download report: ",
                      err?.response?.data?.msg
                    );
                    setErrorMsg(err?.response?.msg || err?.response?.data?.msg);
                    setErrorModal(true);
                  })
                  .finally(() => {
                    setIsOfflineWADetailedReportTableEnabled(true);
                  });
              } else {
                getReports({
                  queryKey: [
                    "MtDetailDownload",
                    userDetails.token,
                    values,
                    "csv",
                    "MT_DETAIL",
                    loggedInRole,
                  ],
                })
                  .then(() => {})
                  .catch((err) => {
                    console.log(
                      "error while trying to call download report: ",
                      err?.response?.data?.msg
                    );
                    setErrorMsg(err?.response?.msg || err?.response?.data?.msg);
                    setErrorModal(true);
                  })
                  .finally(() => {
                    setIsOfflineDetailedReportTableEnabled(true);
                  });
              }
            }
            setIsPreviewSampleClicked(false);
            setIsGenerateReportClicked(true);
          }
        }}
      >
        {({
          errors,
          status,
          touched,
          values,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          handleChange,
          setValues,
          isValid,
          resetForm,
        }) => (
          <Form>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
              <div>
                <label className={styles.labelStyles}>{t("_Channel")}*</label>
                <Dropdown
                  isSearchable={false}
                  name="channel"
                  options={channelOpts}
                  borderRadius="0.25rem"
                  height="2.75rem"
                  placeholder={t("_Select_Channel")}
                  backgroundColor="transparent"
                  onBlur={() => {
                    setFieldTouched("channel", true);
                  }}
                  onChange={(option) => {
                    if (option.value !== values.channel)
                      setValues((prevValues) => ({
                        ...prevValues,
                        interface: "",
                        direction: "",
                        category: "",
                        status: "",
                        bind: "",
                        campNames: null,
                        temp_id: null,
                        channel: option.value,
                      }));
                  }}
                  value={values.channel}
                />
                <ErrorMessage
                  name="channel"
                  component="div"
                  style={{ color: theme.primaryColor }}
                  className=" text-xs"
                />
              </div>
              {values.channel === "voice" && (
                <div>
                  <label className={styles.labelStyles}>
                    {t("_Product_Type")}*
                  </label>
                  <Dropdown
                    isSearchable={false}
                    name="productType"
                    options={voiceproductOptions}
                    borderRadius="0.25rem"
                    height="2.75rem"
                    placeholder={t("_Select_product_type")}
                    backgroundColor="transparent"
                    onBlur={() => {
                      setFieldTouched("productType", true);
                    }}
                    onChange={(option) => {
                      setFieldValue("productType", option.value);
                    }}
                    value={values.productType}
                  />
                  <ErrorMessage
                    name="productType"
                    component="div"
                    style={{ color: theme.primaryColor }}
                    className=" text-xs"
                  />
                </div>
              )}
              <div>
                <label className={styles.labelStyles}>
                  {t("_Date_range")}*
                </label>
                <FilterDate
                  withTimeStamp
                  from="report"
                  defaultLabel={t("_Select_Date_Range")}
                  reportType={reportType}
                  additionalFilter={true}
                  onOpen={(open) => {
                    if (!open) setFieldTouched("startDate", true);
                  }}
                  customDays={
                    props?.type === "detailed"
                      ? props?.dateConfig?.detailedCustomDays
                      : props?.dateConfig?.summaryCustomDays
                  }
                  lastDays={
                    props?.type === "detailed"
                      ? props?.dateConfig?.detailedLastDays
                      : props?.dateConfig?.summaryLastDays
                  }
                  className="pl-3 text-left block w-full h-11 border border-solid border-gray-300 focus:ring-0 focus:shadow-none text-base rounded bg-transparent placeholder-Silver report-date-picker"
                  isBordedApplied={false}
                  setFilterStartDate={() => {}}
                  setFilterEndDate={() => {}}
                  setFieldTouched={setFieldTouched}
                  setDates={(dates) => {
                    let start, end;
                    if (dates.length > 2 && dates[2] === "custom") {
                      start =
                        dates[0] &&
                        dayjs(new Date(dates[0])).format("YYYY-MM-DD") +
                          "T00:00:00.000" +
                          "Z";
                      end =
                        dates[1] &&
                        dayjs(new Date(dates[1])).format("YYYY-MM-DD") +
                          "T23:59:59.999" +
                          "Z";
                    } else {
                      start = dates[0] && dates[0] + "Z";
                      end = dates[1] && dates[1] + "Z";
                    }
                    setValues((vals) => ({
                      ...vals,
                      startDate: start,
                      endDate: end,
                    }));
                  }}
                  boxShadowVisible={false}
                />
                {((errors.startDate && touched.startDate) ||
                  (errors.endDate && touched.endDate)) && (
                  <div
                    style={{ color: theme.primaryColor }}
                    className="text-xs"
                  >
                    Start and End Dates are required
                  </div>
                )}
              </div>

              {loggedInRole !== "EU" && values.channel !== "email" && (
                <div>
                  <label className={styles.labelStyles}>{`${
                    loggedInRole === "PA" ? t("_Account") : t("_Users")
                  }`}</label>
                  <PaginatedDropdown
                    apiCall={
                      loggedInRole === "PA"
                        ? getEnterprisesForReports
                        : getUsersForReports
                    }
                    name="user"
                    placeholder={`${t("_Select")} ${
                      loggedInRole === "PA" ? t("_Account") : t("_user")
                    }`}
                    setFieldTouched={setFieldTouched}
                    setValues={setValues}
                    value={values.user}
                  />
                </div>
              )}

              {loggedInRole !== "EU" && values.channel === "email" && (
                <div>
                  <label className={styles.labelStyles}>{`${
                    loggedInRole === "PA" ? t("_Account") : t("_Users")
                  }`}</label>
                  <PaginatedDropdown
                    apiCall={
                      loggedInRole === "PA"
                        ? getEnterprisesForReports
                        : getUsersForReportsEmail
                    }
                    name="user"
                    placeholder={`${
                      loggedInRole === "PA" ? t("_Account") : t("_Select_users")
                    }`}
                    setFieldTouched={setFieldTouched}
                    setValues={setValues}
                    value={values.user}
                  />
                </div>
              )}

              {values.channel !== "voice" && values.channel !== "email" && (
                <div>
                  <label className={styles.labelStyles}>{t("_Country")}</label>
                  <Dropdown
                    isSearchable={true}
                    name="country"
                    options={countryOptions}
                    borderRadius="0.25rem"
                    height="2.75rem"
                    placeholder={t("_Select_country")}
                    backgroundColor="transparent"
                    onBlur={() => {
                      setFieldTouched("country", true);
                    }}
                    onChange={(option) => {
                      setFieldValue("country", option.value);
                    }}
                    value={values.country}
                  />
                </div>
              )}
              {values.channel !== "voice" &&
                values?.channel !== "wa" &&
                values.channel !== "email" && (
                  <div>
                    <label className={styles.labelStyles}>
                      {t("_Sender_Addresses")}
                    </label>
                    <PaginatedDropdown
                      apiCall={
                        values.user?.value === "" ||
                        values.user?.value === "all" ||
                        values.user?.ownerId === ""
                          ? loggedInRole === "PA"
                            ? getPASenderAddressListForReport
                            : getSenderAddressesForReport
                          : getAllSenderAddressByuser
                      }
                      user={values.user?.ownerId || values.user?.value || ""}
                      country={values.country || ""}
                      name="sender"
                      placeholder={t("_Select_sender_address")}
                      setFieldTouched={setFieldTouched}
                      setValues={setValues}
                      value={values.sender}
                    />
                  </div>
                )}
              {values.channel === "email" && (
                <div>
                  <label className={styles.labelStyles}>
                    {t("_Email_Addresses")}
                  </label>
                  <PaginatedDropdown
                    apiCall={
                      values.user === "" ||
                      values.user?.value === "" ||
                      values.user?.value === "all" ||
                      values.user?.value === "self"
                        ? loggedInRole === "PA"
                          ? getPAEmailAddressListForReport
                          : getEmailAddressesForReport
                        : getAllEmailAddressByuser
                    }
                    user={values.user?.value || ""}
                    name="sender"
                    placeholder={t("_Select_Email")}
                    setFieldTouched={setFieldTouched}
                    setValues={setValues}
                    value={values.account}
                  />
                </div>
              )}
              {values?.channel === "wa" && (
                <div>
                  <label className={styles.labelStyles}>
                    {t("_WABA_Number")}
                  </label>
                  <PaginatedDropdown
                    apiCall={getClients}
                    user={
                      loggedInRole === "PA"
                        ? values?.user?.ownerId || ""
                        : values?.user?.value || ""
                    }
                    country={values.country || ""}
                    name="whatsAppnumber"
                    placeholder={t("_Select_WABA_Number")}
                    setFieldTouched={setFieldTouched}
                    setValues={setValues}
                    value={values.whatsAppnumber}
                  />
                </div>
              )}
              {!(
                (isSummaryReport &&
                  values.channel === "voice" &&
                  values.productType === "Voice") ||
                (isDetailedReport &&
                  values.channel === "voice" &&
                  values.productType === "Voice" &&
                  loggedInRole === "PA") ||
                (loggedInRole === "EA" &&
                  isSummaryReport &&
                  values.productType === "VR" &&
                  values.channel === "voice")
              ) && (
                <div>
                  <label className={styles.labelStyles}>
                    {isWAChannel(values?.channel)
                      ? t("_Category")
                      : t("_Direction")}
                  </label>
                  <Dropdown
                    isSearchable={false}
                    name="direction"
                    options={getOptions(
                      dropdownNames.CATEGORY,
                      values?.channel
                    )}
                    borderRadius="0.25rem"
                    height="2.75rem"
                    placeholder={
                      values?.channel === "wa"
                        ? t("_Select_Category")
                        : t("_Select_Direction")
                    }
                    backgroundColor="transparent"
                    onBlur={() => {
                      setFieldTouched("direction", true);
                    }}
                    onChange={(option) => {
                      setFieldValue("direction", option.value);
                    }}
                    value={values.direction}
                  />
                </div>
              )}
              {enableAuthConfig &&
                (values?.channel === "sms" || values?.channel === "email") && (
                <div>
                  <label className={styles.labelStyles}>{t("_Category")}</label>
                  <Dropdown
                    isSearchable={false}
                    name="category"
                    options={getOptions(
                      dropdownNames.CAMPAIGN_CATEGORY,
                      values?.channel
                    )}
                    borderRadius="0.25rem"
                    height="2.75rem"
                    placeholder={t("_Select_Category")}
                    backgroundColor="transparent"
                    onBlur={() => {
                      setFieldTouched("category", true);
                    }}
                    onChange={(option) => {
                      setFieldValue("category", option.value);
                    }}
                    value={values.category}
                  />
                </div>
              )}
              {!(values.channel === "voice" && values.productType === "VR") && (
                <div>
                  <label className={styles.labelStyles}>
                    {t("_Interface")}
                  </label>
                  <Dropdown
                    isSearchable={false}
                    name="interface"
                    options={getOptions(
                      dropdownNames.INTERFACE,
                      values?.channel
                    )}
                    borderRadius="0.25rem"
                    height="2.75rem"
                    placeholder={t("_Select_Interface")}
                    backgroundColor="transparent"
                    onBlur={() => {
                      setFieldTouched("interface", true);
                    }}
                    onChange={(option) => {
                      setFieldValue("interface", option.value);
                    }}
                    value={values.interface}
                  />
                </div>
              )}

              {props.type !== reportTypes.SUMMARY ? (
                values?.channel === "voice" ? null : (
                  <div>
                    <label className={styles.labelStyles}>{t("_Status")}</label>
                    <Dropdown
                      isSearchable={false}
                      name="status"
                      options={getOptions(
                        dropdownNames.STATUS,
                        values?.channel
                      )}
                      borderRadius="0.25rem"
                      height="2.75rem"
                      placeholder={t("_Select_status")}
                      backgroundColor="transparent"
                      onBlur={() => {
                        setFieldTouched("status", true);
                      }}
                      onChange={(option) => {
                        setFieldValue("status", option.value);
                      }}
                      value={values.status}
                    />
                  </div>
                )
              ) : null}

              {loggedInRole === "PA" &&
                !(values.channel === "voice" && values.productType === "VR") &&
                !(
                  values.channel === "voice" &&
                  values.productType === "Voice" &&
                  isSummaryReport
                ) &&
                !(values?.channel === "email" || values?.channel === "wa") && (
                  <>
                    <div>
                      <label className={styles.labelStyles}>
                        {t("_Provider")}
                      </label>
                      <PaginatedDropdown
                        apiCall={getProvidersForReport}
                        name="provider"
                        channel={values.channel}
                        placeholder={t("_Select_Provider")}
                        setFieldTouched={setFieldTouched}
                        setValues={setValues}
                        value={values.provider}
                      />
                    </div>
                    {values.channel !== "voice" && values?.channel !== "wa" && (
                      <div>
                        <label className={styles.labelStyles}>
                          {t("_Bind")}
                        </label>
                        <Dropdown
                          isSearchable={false}
                          name="bind"
                          disabled={
                            values.channel === "wa" ||
                            !values.provider ||
                            values.provider?.value?.toLowerCase() === "all"
                          }
                          options={bindOptions}
                          borderRadius="0.25rem"
                          height="2.75rem"
                          placeholder={t("_Select_Bind")}
                          backgroundColor={
                            !values.provider ||
                            values.provider?.value?.toLowerCase() === "all"
                              ? "#F4F5F7"
                              : "transparent"
                          }
                          onBlur={() => {
                            setFieldTouched("bind", true);
                          }}
                          onChange={(option) => {
                            setFieldValue("bind", option.label);
                          }}
                          value={values.bind}
                        />
                      </div>
                    )}
                  </>
                )}
              {props.type !== reportTypes.SUMMARY &&
                values.channel !== "email" &&
                !(values.channel === "voice" && loggedInRole === "EA") &&
                !(values.productType === "VR" && values.channel === "voice") &&
                !(
                  values.channel === "voice" &&
                  values.productType === "Voice" &&
                  isSummaryReport
                ) && (
                  <div>
                    <label className={styles.labelStyles}>MSISDN</label>
                    <Field
                      type="text"
                      placeholder={t("_Enter_MSISDN")}
                      name="msisdn"
                      onFocus={(e) => {
                        e.target.style.borderColor = theme.secondaryColor;
                        e.target.style.boxShadow = `0 0 2px ${theme.secondaryColor}`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#CACACA";
                        e.target.style.boxShadow = "none";
                        setFieldTouched("msisdn", true);
                      }}
                      style={{
                        border: "1px solid",
                        borderColor: "#CACACA",
                      }}
                      className="bg-transparent w-full h-11 text-sm placeholder-[#CACACA] bg-clip-padding rounded focus:outline-none text-black font-normal"
                    />
                    <ErrorMessage
                      name="msisdn"
                      component="div"
                      style={{ color: theme.primaryColor }}
                      className=" text-xs"
                    />
                  </div>
                )}

              {values.productType === "Voice" &&
                values.channel === "voice" &&
                loggedInRole === "PA" &&
                isDetailedReport && (
                  <div>
                    <label className={styles.labelStyles}>Campaign Name</label>

                    <Field
                      type="text"
                      placeholder="Enter campaign name"
                      name="campaignName"
                      onFocus={(e) => {
                        e.target.style.borderColor = theme.secondaryColor;
                        e.target.style.boxShadow = `0 0 2px ${theme.secondaryColor}`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#CACACA";
                        e.target.style.boxShadow = "none";
                        setFieldTouched("campaignName", true);
                      }}
                      style={{
                        border: "1px solid",
                        borderColor: "#CACACA",
                      }}
                      className="bg-transparent w-full h-11 text-sm placeholder-[#CACACA] bg-clip-padding  rounded   focus:outline-none text-black font-semibold"
                    />
                  </div>
                )}
              {values.productType === "Voice" &&
                values.channel === "voice" &&
                loggedInRole === "PA" &&
                isDetailedReport && (
                  <div>
                    <label className={styles.labelStyles}>Audio Source</label>
                    <Dropdown
                      isSearchable={false}
                      name="audioSource"
                      options={audioSourceOptions}
                      borderRadius="0.25rem"
                      height="2.75rem"
                      placeholder="Select audio source"
                      backgroundColor="transparent"
                      onBlur={() => {
                        setFieldTouched("audioSource", true);
                      }}
                      onChange={(option) => {
                        setFieldValue("audioSource", option.value);
                      }}
                      value={values.audioSource}
                    />
                  </div>
                )}

              {values.channel === "voice" &&
                props.type !== reportTypes.SUMMARY && (
                  <div>
                    <label className={styles.labelStyles}>
                      {t("_Final_status")}
                    </label>
                    <Dropdown
                      isSearchable={false}
                      name="finalStatus"
                      options={finalStatusOptionsForVoice}
                      borderRadius="0.25rem"
                      height="2.75rem"
                      placeholder={t("_Select_status")}
                      backgroundColor="transparent"
                      onBlur={() => {
                        setFieldTouched("finalStatus", true);
                      }}
                      onChange={(option) => {
                        setFieldValue("finalStatus", option.value);
                      }}
                      value={values.finalStatus}
                    />
                  </div>
                )}
              {values.channel === "voice" &&
                values.productType === "Voice" &&
                props.type !== reportTypes.SUMMARY &&
                loggedInRole === "PA" && (
                  <div>
                    <label className={styles.labelStyles}>
                      {" "}
                      Display Status
                    </label>
                    <Dropdown
                      isSearchable={false}
                      name="displayStatus"
                      options={statusOptions}
                      borderRadius="0.25rem"
                      height="2.75rem"
                      placeholder={t("_Select_status")}
                      backgroundColor="transparent"
                      onBlur={() => {
                        setFieldTouched("displayStatus", true);
                      }}
                      onChange={(option) => {
                        setFieldValue("displayStatus", option.value);
                      }}
                      value={values.displayStatus}
                    />
                  </div>
                )}
              {values.productType === "VR" && values.channel === "Voice" && (
                <div>
                  <label className={styles.labelStyles}>Enterprise No.</label>
                  <PaginatedDropdown
                    apiCall={getVREnabledNumberList}
                    name="enterpriseNo"
                    placeholder="Select enterprise no."
                    setFieldTouched={setFieldTouched}
                    setValues={setValues}
                    value={values.enterpriseNo}
                  />
                  <ErrorMessage
                    name="enterpriseNo"
                    component="div"
                    style={{ color: theme.primaryColor }}
                    className=" text-xs"
                  />
                </div>
              )}
              {loggedInRole === "PA" &&
                values.productType === "VR" &&
                values.channel === "voice" && (
                  <div>
                    <label className={styles.labelStyles}>
                      Enterprise User No.
                    </label>

                    <Field
                      type="text"
                      placeholder="Enter Enterprise user no."
                      name="enterpriseUserNo"
                      onFocus={(e) => {
                        e.target.style.borderColor = theme.secondaryColor;
                        e.target.style.boxShadow = `0 0 2px ${theme.secondaryColor}`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#CACACA";
                        e.target.style.boxShadow = "none";
                        setFieldTouched("enterpriseUserNo", true);
                      }}
                      style={{
                        border: "1px solid",
                        borderColor: "#CACACA",
                      }}
                      className="bg-transparent w-full h-11 text-sm placeholder-[#CACACA] bg-clip-padding  rounded   focus:outline-none text-black font-semibold"
                    />
                    <ErrorMessage
                      name="enterpriseUserNo"
                      component="div"
                      style={{ color: theme.primaryColor }}
                      className=" text-xs"
                    />
                  </div>
                )}
              {loggedInRole === "PA" &&
                values.productType === "VR" &&
                values.channel === "voice" && (
                  <div>
                    <label className={styles.labelStyles}>Customer No.</label>
                    <Field
                      type="text"
                      placeholder="Enter customerNo no."
                      name="customerNo"
                      onFocus={(e) => {
                        e.target.style.borderColor = theme.secondaryColor;
                        e.target.style.boxShadow = `0 0 2px ${theme.secondaryColor}`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#CACACA";
                        e.target.style.boxShadow = "none";
                        setFieldTouched("customerNo", true);
                      }}
                      style={{
                        border: "1px solid",
                        borderColor: "#CACACA",
                      }}
                      className="bg-transparent w-full h-11 text-sm placeholder-[#CACACA] bg-clip-padding  rounded   focus:outline-none text-black font-semibold"
                    />
                    <ErrorMessage
                      name="customerNo"
                      component="div"
                      style={{ color: theme.primaryColor }}
                      className=" text-xs"
                    />
                  </div>
                )}
              {values.productType === "Voice" &&
                loggedInRole === "PA" &&
                values.channel === "voice" && (
                  <div>
                    <label className={styles.labelStyles}>Caller ID</label>
                    <PaginatedDropdown
                      apiCall={getVoiceCapabilityNumberList}
                      name="callerId"
                      placeholder="Select caller Id"
                      setFieldTouched={setFieldTouched}
                      setValues={setValues}
                      value={values.callerId}
                      accountId={values.user ? values.user.ownerId : ""}
                    />
                  </div>
                )}
              {values.productType === "Voice" && values.channel === "voice" && (
                <div>
                  <label className={styles.labelStyles}>
                    Campaign Category
                  </label>
                  <Dropdown
                    isSearchable={false}
                    name="campaignCategory"
                    options={campaignCategoryOptionsForVoice}
                    borderRadius="0.25rem"
                    height="2.75rem"
                    placeholder="Select campaign category"
                    backgroundColor="transparent"
                    onBlur={() => {
                      setFieldTouched("campaignCategory", true);
                    }}
                    onChange={(option) => {
                      setFieldValue("campaignCategory", option.value);
                    }}
                    value={values.campaignCategory}
                  />
                </div>
              )}

              {values?.channel === "wa" && isSummaryReport ? null : (
                <div>
                  <label className={styles.labelStyles}>
                    {t("_Campaign_ID")}
                  </label>
                  <Field
                    type="text"
                    placeholder={t("_Enter_Campaign_ID")}
                    name="campaignId"
                    className="bg-transparent w-full h-11 text-sm placeholder-[#CACACA] bg-clip-padding border border-solid border-gray-300 rounded  focus:border-blue-600 focus:outline-none text-black font-normal"
                  />
                  <ErrorMessage
                    name="campaignId"
                    component="div"
                    className="text-[#FF6E6E] text-xs"
                  />
                </div>
              )}
              {values?.channel === "email" && isDetailedReport && (
                <div>
                  <label className={styles.labelStyles}>{t("_Email_ID")}</label>
                  <Field
                    type="text"
                    placeholder={t("_Enter_Email_ID")}
                    name="emailid"
                    className="bg-transparent w-full h-11 text-sm placeholder-[#CACACA] bg-clip-padding border border-solid border-gray-300 rounded  focus:border-blue-600 focus:outline-none text-black font-normal"
                  />
                  <ErrorMessage
                    name="emailid"
                    component="div"
                    className="text-[#FF6E6E] text-xs"
                  />
                </div>
              )}

              <div>
                <label className={styles.labelStyles}>
                  {t("_Campaign_Name")}
                </label>

                <MultiSelectPaginatedDropdown
                  name="campNames"
                  placeholder={t("_Select_Campaign")}
                  values={values.campNames}
                  setValues={setValues}
                  apiCall={getCampList}
                  token={userDetails.token}
                  setFieldTouched={setFieldTouched}
                  selectedChannel={values.channel}
                  isDisabled={
                    values.channel === "" ||
                    values.startDate === "" ||
                    values.endDate === ""
                  }
                  cacheUniqs={[
                    getCampList,
                    values.channel,
                    values.startDate,
                    values.endDate,
                  ]}
                  loadOptions={async (
                    search,
                    prevOptions,
                    { page: pgIndex }
                  ) => {
                    let apiResponse;
                    apiResponse = await getCampList(
                      userDetails.token,
                      pgIndex,
                      values.channel === "wa" ? "whatsapp" : values.channel,
                      search,
                      dayjs(values.startDate).toDate().toLocaleString("sv-SE"),
                      dayjs(values.endDate)
                        .subtract(1, "day")
                        .toDate()
                        .toLocaleString("sv-SE")
                    );

                    if (pgIndex === 1) {
                      uniqueCampaignNameSet = new Set();
                    }
                    let uniqueOptions = [];
                    apiResponse.options.forEach((item) => {
                      if (!uniqueCampaignNameSet.has(item.value)) {
                        uniqueOptions.push(item);
                        uniqueCampaignNameSet.add(item.value);
                      }
                    });
                    return {
                      options: uniqueOptions,
                      hasMore: apiResponse.hasMore,
                      additional: { page: pgIndex + 1 },
                    };
                  }}
                />
                <ErrorMessage
                  name="templateIds"
                  component="div"
                  className="text-[#FF6E6E] text-xs"
                />
              </div>

              {values?.channel === "sms" && isDetailedReport && (
                <div>
                  <label className={styles.labelStyles}>
                    {t("_Client_Txn_ID")}
                  </label>
                  <Field
                    type="text"
                    placeholder={t("_Enter_Client_Txn_ID")}
                    name="clientTxnId"
                    className="bg-transparent w-full h-11 text-sm placeholder-[#CACACA] bg-clip-padding border border-solid border-gray-300 rounded  focus:border-blue-600 focus:outline-none text-black font-normal"
                  />
                  <ErrorMessage
                    name="clientTxnId"
                    component="div"
                    className="text-[#FF6E6E] text-xs"
                  />
                </div>
              )}

              {values?.channel === "wa" && isDetailedReport && (
                <div>
                  <label className={styles.labelStyles}>
                    {t("_Template_Name")}
                  </label>

                  <MultiSelectPaginatedDropdown
                    name="temp_id"
                    placeholder={t("_Select_Template")}
                    values={values.temp_id}
                    setValues={setValues}
                    apiCall={getTemplateList}
                    token={userDetails.token}
                    setFieldTouched={setFieldTouched}
                    selectedChannel={values.channel}
                    isDisabled={
                      values.channel === "" ||
                      values.startDate === "" ||
                      values.endDate === ""
                    }
                    cacheUniqs={[
                      getTemplateList,
                      values.channel,
                      values.startDate,
                      values.endDate,
                    ]}
                    loadOptions={async (
                      search,
                      prevOptions,
                      { page: pgIndex }
                    ) => {
                      let apiResponse = await getTemplateList(
                        userDetails.token,
                        pgIndex,
                        values.channel === "wa" ? "whatsapp" : values.channel,
                        search,
                        values.startDate?.replace("Z", getTimeZoneInHours()),
                        values.endDate?.replace("Z", getTimeZoneInHours())
                      );

                      if (pgIndex === 1) {
                        uniqueTemplateSet = new Set();
                      }
                      let uniqueOptions = [];
                      apiResponse.options.forEach((item) => {
                        if (!uniqueTemplateSet.has(item.value)) {
                          uniqueOptions.push(item);
                          uniqueTemplateSet.add(item.value);
                        }
                      });

                      return {
                        options: uniqueOptions,
                        hasMore: apiResponse.hasMore,
                        additional: { page: pgIndex + 1 },
                      };
                    }}
                  />
                </div>
              )}

              {filterConfig.emailFilterEnabled && isSummaryReport && (
                <InputFieldComponent
                  label={t("_Email_ID")}
                  placeholder={t("_Enter_Email_ID")}
                  name="userEmail"
                  key={"userEmail"}
                />
              )}
              {filterConfig.orgIdFilterEnabled && isSummaryReport && (
                <InputFieldComponent
                  label={t("_Organization_ID")}
                  placeholder={t("_Enter_Organization_ID")}
                  name="organizationId"
                  key={"organizationId"}
                />
              )}
            </div>

            <div className="mt-5 text-right gap-5 flex justify-end">
              <PrimaryButton
                text={"_PREVIEW_SAMPLE"}
                disabled={!isValid}
                onClick={async () => {
                  handleSubmit(values);
                  setIsButtonClicked("PreviewSample");
                  setIsGenerateReportClicked(false);
                  setIsPreviewSampleClicked(true);
                }}
              />
              <SecondaryButton
                type="button"
                text={"_Generate_Report"}
                disabled={!isValid}
                onClick={() => {
                  setIsButtonClicked("GenerateReport");
                  setIsGenerateReportClicked(true);
                  setIsPreviewSampleClicked(false);
                  if (!reportNamePromptEnabled) {
                    handleSubmit(values);
                    return;
                  }
                  setPendingDownload({ values, handleSubmit });
                  setShowDownloadNameModal(true);
                }}
              />
            </div>

            <DownloadReportModal
              isOpen={reportNamePromptEnabled && showDownloadNameModal}
              defaultName={
                userDetails?.userName
                  ? `${userDetails.userName}_SummaryReport_<timestamp>`
                  : ""
              }
              onCancel={() => {
                setShowDownloadNameModal(false);
                setPendingDownload(null);
              }}
              onConfirm={(rptName) => {
                setShowDownloadNameModal(false);
                if (!pendingDownload) return;
                // Empty name -> parameter is omitted downstream and the backend
                // keeps the name it has always generated.
                pendingDownload.values.rptName = rptName || "";
                pendingDownload.handleSubmit(pendingDownload.values);
                setPendingDownload(null);
              }}
            />

            <MainDiv from="tablecard" className="mt-5">
              <div className="text-sm flex mb-5 justify-between items-center">
                <p className="font-bold">
                  <div className="flex">
                    <div>{t("_Requested_Reports")}</div>
                    {!isPreviewSampleClicked && (
                      <div
                        onMouseEnter={() => setOpenInfoModal(true)}
                        onMouseLeave={() => setOpenInfoModal(false)}
                      >
                        <InfoIcon
                          alt="info"
                          src={infoIcon}
                          style={{
                            display: "inline-block",
                          }}
                          className="mx-1 mb-1"
                        />
                        {openInfoModal && (
                          <div className="relative">
                            <InfoModal
                              closeInfoModal={() => setOpenInfoModal(false)}
                              type="th"
                            >
                              <span className="my-3 font-bold text-md text-gray-500">
                                {t(
                                  "Note_The_requests_that_were_made_within_the"
                                )}
                                {t("last")}{" "}
                                {props?.type === "detailed"
                                  ? props?.dateConfig?.detailedCustomDays
                                  : props?.dateConfig?.summaryCustomDays}{" "}
                                {t("days_will_be_fulfilled.")}
                              </span>
                            </InfoModal>
                          </div>
                        )}
                      </div>
                    )}
                    {isPreviewSampleClicked && values?.channel == "wa" && (
                      <div
                        onMouseEnter={() => setOpenInfoModal(true)}
                        onMouseLeave={() => setOpenInfoModal(false)}
                      >
                        <InfoIcon
                          alt="info"
                          src={infoIcon}
                          style={{
                            display: "inline-block",
                          }}
                          className="mx-1 mb-1"
                        />
                        {openInfoModal && (
                          <div className="relative">
                            <InfoModal
                              closeInfoModal={() => setOpenInfoModal(false)}
                              type="th"
                            >
                              <span className="my-3 font-bold text-[12px] text-gray-500 bg-Gallery">
                                {reportType == reportTypes.SUMMARY
                                  ? t("_Summary_Report_WA_NOTE_Conv_Change")
                                  : t("_Detailed_Report_WA_NOTE_Conv_Change")}
                              </span>
                            </InfoModal>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </p>
                {!isPreviewSampleClicked && (
                  // <button
                  //   disabled={isRefreshDisabled}
                  //   style={
                  //     isRefreshDisabled
                  //       ? {
                  //           color: theme.primaryColor,
                  //           border: `1px solid ${theme.primaryColor}`,
                  //           opacity: "53%",
                  //         }
                  //       : {
                  //           color: theme.primaryColor,
                  //           border: `1px solid ${theme.primaryColor}`,
                  //         }
                  //   }
                  //   onClick={() => {
                  //     if (reportType === "summary") {
                  //       if (values?.channel === "email") {
                  //         setIsOfflineEmailSummaryReportTableEnabled(true);
                  //       } else if (values?.channel === "wa") {
                  //         setIsOfflineWASummaryReportTableEnabled(true);
                  //       } else if (values.channel === "voice") {
                  //         setIsOfflineVoiceSummaryReportTableEnabled(true);
                  //       } else {
                  //         setIsOfflineSummaryReportTableEnabled(true);
                  //       }
                  //     } else {
                  //       if (values?.channel === "email") {
                  //         setIsOfflineEmailDetailedReportTableEnabled(true);
                  //       } else if (values?.channel === "wa") {
                  //         setIsOfflineWADetailedReportTableEnabled(true);
                  //       } else if (values.channel === "voice") {
                  //         setIsOfflineVoiceDetailedReportTableEnabled(true);
                  //       } else {
                  //         setIsOfflineDetailedReportTableEnabled(true);
                  //       }
                  //     }
                  //     setIsRefreshDisabled(true);
                  //   }}
                  //   className="text-sm uppercase font-bold px-5 py-1 rounded-full bg-white ltr:mr-5 rtl:ml-5 disabled:cursor-not-allowed"
                  // >
                  //   {t("_REFRESH")}
                  // </button>
                  <IconButton
                    iconName="RefreshIconDual"
                    title={t("_Refresh")}
                    disabled={isRefreshDisabled}
                    onClick={() => {
                      if (reportType === reportTypes.SUMMARY) {
                        if (values?.channel === "email") {
                          setIsOfflineEmailSummaryReportTableEnabled(true);
                        } else if (values?.channel === "wa") {
                          setIsOfflineWASummaryReportTableEnabled(true);
                        } else if (values.channel === "voice") {
                          setIsOfflineVoiceSummaryReportTableEnabled(true);
                        } else {
                          setIsOfflineSummaryReportTableEnabled(true);
                        }
                      } else {
                        if (values?.channel === "email") {
                          setIsOfflineEmailDetailedReportTableEnabled(true);
                        } else if (values?.channel === "wa") {
                          setIsOfflineWADetailedReportTableEnabled(true);
                        } else if (values.channel === "voice") {
                          setIsOfflineVoiceDetailedReportTableEnabled(true);
                        } else {
                          setIsOfflineDetailedReportTableEnabled(true);
                        }
                      }
                      setIsRefreshDisabled(true);
                    }}
                  />
                )}
              </div>
              <div className="w-full">
                {isPreviewSampleClicked &&
                  (reportType === reportTypes.SUMMARY ? (
                    values.channel === "voice" ? (
                      <PreviewSampleForSummaryForVoice
                        productType={values.productType}
                        isFetchingNextPage={voiceSummaryIsFetchingNextPage}
                        fetchNextPage={voiceSummaryFetchNextPage}
                        isLoading={isVoiceSummaryLoading}
                        hasNextPage={voiceSummaryHasNextPage}
                        loggedInRole={loggedInRole}
                        data={voiceSummaryData}
                      />
                    ) : values?.channel === "wa" ? (
                      <PreviewSampleForSummaryForWA
                        isFetchingNextPage={waSummaryIsFetchingNextPage}
                        fetchNextPage={waSummaryViewFetchNextPage}
                        isLoading={isWaSummaryLoading}
                        hasNextPage={waSummaryHasNextPage}
                        loggedInRole={loggedInRole}
                        data={waSummaryData}
                        direction={values?.direction}
                        globalConfig={globalConfig}
                      />
                    ) : values?.channel === "email" ? (
                      <PreviewSampleForSummaryForEmail
                        productType={values.productType}
                        isFetchingNextPage={emailSummaryIsFetchingNextPage}
                        fetchNextPage={emailSummaryFetchNextPage}
                        isLoading={isEmailSummaryLoading}
                        hasNextPage={emailSummaryHasNextPage}
                        loggedInRole={loggedInRole}
                        data={emailSummaryData}
                      />
                    ) : (
                      <PreviewSampleForSummary
                        isFetchingNextPage={summaryIsFetchingNextPage}
                        fetchNextPage={mtSummaryViewFetchNextPage}
                        isLoading={isSummaryLoading}
                        hasNextPage={summaryHasNextPage}
                        loggedInRole={loggedInRole}
                        data={summaryData}
                        direction={values?.direction}
                      />
                    )
                  ) : values.channel === "voice" ? (
                    <PreviewSampleForDetailsForVoice
                      productType={values.productType}
                      isFetchingNextPage={voicedetailsIsFetchingNextPage}
                      fetchNextPage={voicedetailsFetchNextPage}
                      isLoading={isVoiceDetailsLoading}
                      hasNextPage={voicedetailsHasNextPage}
                      loggedInRole={loggedInRole}
                      data={voicedetailsData}
                    />
                  ) : values?.channel === "email" ? (
                    <PreviewSampleForDetailsForEmail
                      isFetchingNextPage={emailDetailsIsFetchingNextPage}
                      fetchNextPage={emailDetailsFetchNextPage}
                      isLoading={isEmailDetailsLoading}
                      hasNextPage={emailDetailsHasNextPage}
                      loggedInRole={loggedInRole}
                      data={emailDetailsData}
                      theme={theme}
                    />
                  ) : values?.channel === "wa" ? (
                    <PreviewSampleForDetailsForWA
                      isFetchingNextPage={waDetailsIsFetchingNextPage}
                      fetchNextPage={waDetailsFetchNextPage}
                      isLoading={isWaDetailsLoading}
                      hasNextPage={waDetailsHasNextPage}
                      loggedInRole={loggedInRole}
                      data={waDetailsData}
                      direction={values?.direction}
                      theme={theme}
                    />
                  ) : values?.channel === "rcs" ? (
                    <PreviewSampleForDetailsForRCS
                      isFetchingNextPage={detailsIsFetchingNextPage}
                      fetchNextPage={detailsFetchNextPage}
                      isLoading={isDetailsLoading}
                      hasNextPage={detailsHasNextPage}
                      loggedInRole={loggedInRole}
                      data={detailsData}
                    />
                  ) : (
                    <PreviewSampleForDetails
                      isFetchingNextPage={detailsIsFetchingNextPage}
                      fetchNextPage={detailsFetchNextPage}
                      isLoading={isDetailsLoading}
                      hasNextPage={detailsHasNextPage}
                      loggedInRole={loggedInRole}
                      direction={values?.direction}
                      data={detailsData}
                    />
                  ))}

                {!isPreviewSampleClicked &&
                  (reportType === reportTypes.SUMMARY ? (
                    values.channel === "voice" ? (
                      <GenerateReport
                        values={values}
                        reportType={reportType}
                        userDetails={userDetails}
                        isLoading={isOfflineVoiceSummaryReportTableDataLoading}
                        isRefreshDisabled={isRefreshDisabled}
                        data={offlineVoiceSummaryReportTableData}
                        setIsOfflineReportTableEnabled={
                          setIsOfflineSummaryReportTableEnabled
                        }
                        theme={theme}
                      />
                    ) : values?.channel === "wa" ? (
                      <GenerateReport
                        values={values}
                        reportType={reportType}
                        userDetails={userDetails}
                        isLoading={isOfflineWaSummaryReportTableDataLoading}
                        isRefreshDisabled={isRefreshDisabled}
                        data={offlineWaSummaryReportTableData}
                        setIsOfflineReportTableEnabled={
                          setIsOfflineWASummaryReportTableEnabled
                        }
                        theme={theme}
                      />
                    ) : values?.channel === "email" ? (
                      <GenerateReport
                        values={values}
                        reportType={reportType}
                        userDetails={userDetails}
                        isLoading={isOfflineEmailSummaryReportTableDataLoading}
                        isRefreshDisabled={isRefreshDisabled}
                        data={offlineEmailSummaryReportTableData}
                        setIsOfflineReportTableEnabled={
                          setIsOfflineEmailSummaryReportTableEnabled
                        }
                        theme={theme}
                      />
                    ) : values?.channel === "rcs" ? (
                      <GenerateReport
                        values={values}
                        reportType={reportType}
                        userDetails={userDetails}
                        isLoading={isOfflineSummaryReportTableDataLoading}
                        isRefreshDisabled={isRefreshDisabled}
                        data={offlineSummaryReportTableData}
                        setIsOfflineReportTableEnabled={
                          setIsOfflineSummaryReportTableEnabled
                        }
                        theme={theme}
                      />
                    ) : (
                      <GenerateReport
                        values={values}
                        reportType={reportType}
                        userDetails={userDetails}
                        isLoading={isOfflineSummaryReportTableDataLoading}
                        isRefreshDisabled={isRefreshDisabled}
                        data={offlineSummaryReportTableData}
                        setIsOfflineReportTableEnabled={
                          setIsOfflineSummaryReportTableEnabled
                        }
                        theme={theme}
                      />
                    )
                  ) : values.channel === "voice" ? (
                    <GenerateReport
                      values={values}
                      reportType={reportType}
                      userDetails={userDetails}
                      isLoading={isOfflineVoiceDetailedReportTableDataLoading}
                      isRefreshDisabled={isRefreshDisabled}
                      data={offlineVoiceDetailedReportTableData}
                      setIsOfflineReportTableEnabled={
                        setIsOfflineDetailedReportTableEnabled
                      }
                      theme={theme}
                    />
                  ) : values?.channel === "wa" ? (
                    <GenerateReport
                      values={values}
                      reportType={reportType}
                      userDetails={userDetails}
                      isLoading={isOfflineWaDetailedReportTableDataLoading}
                      isRefreshDisabled={isRefreshDisabled}
                      data={offlineWaDetailedReportTableData}
                      setIsOfflineReportTableEnabled={
                        setIsOfflineWADetailedReportTableEnabled
                      }
                      theme={theme}
                    />
                  ) : values?.channel === "email" ? (
                    <GenerateReport
                      values={values}
                      reportType={reportType}
                      userDetails={userDetails}
                      isLoading={isOfflineEmailDetailedReportTableDataLoading}
                      isRefreshDisabled={isRefreshDisabled}
                      data={offlineEmailDetailedReportTableData}
                      setIsOfflineReportTableEnabled={
                        setIsOfflineEmailDetailedReportTableEnabled
                      }
                      theme={theme}
                    />
                  ) : (
                    <GenerateReport
                      values={values}
                      reportType={reportType}
                      userDetails={userDetails}
                      isLoading={isOfflineDetailedReportTableDataLoading}
                      isRefreshDisabled={isRefreshDisabled}
                      data={offlineDetailedReportTableData}
                      setIsOfflineReportTableEnabled={
                        setIsOfflineDetailedReportTableEnabled
                      }
                      theme={theme}
                    />
                  ))}
              </div>
            </MainDiv>
          </Form>
        )}
      </Formik>
      <ErrorModal
        deleteError={errorMsg}
        errorModal={errorModal}
        setErrorModal={setErrorModal}
      />
    </div>
  );
};

const fallbackLoading = <h2>{t("_Loading...")}</h2>;

function Reports() {
  const [errorModal, setErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isPreviewSampleClicked, setIsPreviewSampleClicked] = useState(false);
  const [isGenerateReportClicked, setIsGenerateReportClicked] = useState(false);

  const [dropdownValues, setDropdownValues] = useState({
    dateRange: [null, null],
    channel: "",
    status: "",
    user: "",
    sender: "",
    direction: "",
    bind: "",
    provider: "",
    country: "",
    interface: "",
    msisdn: "",
    category: "",
    startDate: "",
    endDate: "",
    defaultDateSelection: false,
    productType: "",
    campaignName: "",
    audioSource: "",
    enterpriseUserNo: "",
    enterpriseNo: "",
    finalStatus: "",
    displayStatus: "",
    callerId: "",
    ratesheetname: "",
    packageType: "",
    campaignId: "",
    clientTxnId: "",
    userEmail: "",
    organizationId: "",
  });
  const { globalConfig } = useAuthContext();
  const { loggedInRole } = useUsersConfig();
  const [reportDateConfig, setReportDateConfig] = useState({});

  //TODO - check if whatsapp and decide what to do in case of whatsapp channel
  const navigationData = [
    { name: t("_Reports"), url: "/app/insights/reports" },
    { name: t("_Usage"), url: "" },
  ];

  const getCustomDays = () => {
    let obj = {
      summaryCustomDays: 90,
      summaryLastDays: 7,
      detailedCustomDays: 7,
      detailedLastDays: 7,
    };
    globalConfig?.map((val) => {
      if (val?.key === "summaryCustomDays")
        obj.summaryCustomDays = parseInt(val?.value) || 90;
      else if (val?.key === "summaryLastDays")
        obj.summaryLastDays = parseInt(val?.value) || 7;
      else if (val?.key === "detailedCustomDays")
        obj.detailedCustomDays = parseInt(val?.value) || 7;
      else if (val?.key === "detailedLastDays")
        obj.detailedLastDays = parseInt(val?.value) || 7;
    });
    setReportDateConfig(obj);
  };

  useEffect(() => {
    getCustomDays();
  }, []);

  return (
    <Suspense fallback={fallbackLoading}>
      <PageContainer>
        <div>
          <div className="flex justify-between items-center">
            <Navigation data={navigationData} />
          </div>
          <TabComponent
            selectedTabId={(tabId) => {
              setDropdownValues({
                dateRange: [null, null],
                channel: "",
                status: "",
                user: "",
                sender: "",
                direction: "",
                bind: "",
                provider: "",
                country: "",
                interface: "",
                msisdn: "",
                category: "",
                startDate: "",
                endDate: "",
                defaultDateSelection: !dropdownValues.defaultDateSelection,
                productType: "",
                campaignName: "",
                audioSource: "",
                enterpriseUserNo: "",
                enterpriseNo: "",
                finalStatus: "",
                displayStatus: "",
                callerId: "",
                ratesheetname: "",
                packageType: "",
                campaignId: "",
                clientTxnId: "",
                campNames: [],
                temp_id: [],
              });
              setIsPreviewSampleClicked(false);
              setIsGenerateReportClicked(false);
            }}
            tabs={{
              [t("_Summary")]: (
                <CommonReports
                  type="summary"
                  isPreviewSampleClicked={isPreviewSampleClicked}
                  setIsPreviewSampleClicked={setIsPreviewSampleClicked}
                  isGenerateReportClicked={isGenerateReportClicked}
                  setIsGenerateReportClicked={setIsGenerateReportClicked}
                  setDropdownValues={setDropdownValues}
                  dropdownValues={dropdownValues}
                  loggedInRole={loggedInRole}
                  dateConfig={reportDateConfig}
                  globalConfig={globalConfig}
                />
              ),

              [t("_Detailed")]: (
                <CommonReports
                  type="detailed"
                  isPreviewSampleClicked={isPreviewSampleClicked}
                  setIsPreviewSampleClicked={setIsPreviewSampleClicked}
                  isGenerateReportClicked={isGenerateReportClicked}
                  setIsGenerateReportClicked={setIsGenerateReportClicked}
                  dropdownValues={dropdownValues}
                  setDropdownValues={setDropdownValues}
                  loggedInRole={loggedInRole}
                  dateConfig={reportDateConfig}
                  globalConfig={globalConfig}
                />
              ),
            }}
          />
        </div>
      </PageContainer>
      <ErrorModal
        deleteError={errorMsg}
        errorModal={errorModal}
        setErrorModal={setErrorModal}
      />
    </Suspense>
  );
}

export default Reports;

